import React, { useEffect, useState, useRef } from 'react';
import { BarLoader } from 'react-spinners';
import { onGameEvent, emitGameEvent } from '../../core/GameEvents';
import { networkManager } from '../../systems/NetworkManager';
import { DropLootModal } from './DropLootModal';
import { ShipBayOverlay } from './ShipBayOverlay';
import { DevSpawnerPanel } from './DevSpawnerPanel';
import { UtilityHotbar } from './UtilityHotbar';
import { NotificationBell } from './NotificationBell';
import { CorporationHUD } from './CorporationHUD';
import { RadarMinimap } from './RadarMinimap';
import { WarpCanvas } from './WarpOverlay';
import { usePanelPositionStore } from './panelPositionStore';
import { GAME_CLOCK } from '@astra/shared';

// Color palette matching the game
const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  YELLOW: '#fbbf24',
  RED: '#ef4444',
  GREEN: '#22c55e',
  POLICE_BLUE: '#3366ff',
};

// Reputation status type
type ReputationStatus = 'neutral' | 'wanted' | 'outlaw' | 'hero';

// Nearby entity type
interface NearbyEntity {
  id: string;
  type: 'player' | 'asteroid' | 'station' | 'drop' | 'waypoint' | 'gateway' | 'hotspot' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage' | 'private_station' | 'beacon';
  name: string;
  distance: number;
  x: number;
  y: number;
  // Ship-specific fields (for players)
  shipName?: string;        // Full ship name (e.g., "Sovereign")
  shipClassType?: string;   // Ship class type (e.g., "dreadnought")
  shipTier?: number;        // Ship tier (1-4)
  shipSize?: string;        // Ship size category (small/medium/large)
  totalPower?: number;      // Total ship power
  hull?: number;            // Current hull
  maxHull?: number;         // Max hull
  shield?: number;          // Current shield
  maxShield?: number;       // Max shield
  // Scanned entity fields
  isScanned?: boolean;      // True if discovered via radar scan
  radius?: number;          // For hotspots, planets, suns, nebulas
  resourceType?: string;    // For hotspots
  // Extra entity data (from GameScene)
  data?: Record<string, unknown>;
}

// Helper to get tier color
function getTierColor(tier: number): string {
  switch (tier) {
    case 1: return PALETTE.GREEN;
    case 2: return '#3b82f6'; // Blue
    case 3: return PALETTE.ORANGE;
    case 4: return PALETTE.RED;
    default: return PALETTE.LIGHT;
  }
}

// Styles
// Two-font system:
// - FONT_DISPLAY: Rajdhani — titles, section headers, buttons, labels (display/condensed)
// - FONT_DATA: JetBrains Mono — values, stats, coordinates, lists, descriptions (data/body)
const FONT_DISPLAY = "'Rajdhani', sans-serif";
const FONT_DATA = "'JetBrains Mono', monospace";

const hudContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  fontFamily: FONT_DATA,
  color: PALETTE.LIGHT,
  fontSize: '10px',
};

// Top Center - Constellation info
const topCenterStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
};

// Bottom Center - Status bars
const bottomCenterStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 16px',
  backgroundColor: `${PALETTE.PANEL}cc`,
  borderRadius: '6px',
};

// Thin horizontal bar for status (HULL, SHIELD, HEAT, BOOST)
function ThinBar({
  label,
  current,
  max,
  color,
  activeColor,
  width = 200,
  isActive = false,
  warningText,
}: {
  label: string;
  current: number;
  max: number;
  color: string;
  activeColor?: string;
  width?: number;
  isActive?: boolean;
  warningText?: string;
}) {
  const percentage = max > 0 ? Math.max(0, Math.min(100, (current / max) * 100)) : 0;
  const displayColor = isActive && activeColor ? activeColor : color;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ width: '60px', textAlign: 'right', color: PALETTE.DIM, fontSize: '9px' }}>
        {label}
      </span>
      <div
        style={{
          width: `${width}px`,
          height: '6px',
          backgroundColor: PALETTE.DARK,
          borderRadius: '1px',
          overflow: 'hidden',
          position: 'relative',
          border: `1px solid ${isActive ? (activeColor || color) : color + '44'}`,
          boxShadow: isActive ? `0 0 8px ${activeColor || color}88, 0 0 16px ${activeColor || color}44` : 'none',
          animation: isActive ? 'overloadBarPulse 0.6s ease-in-out infinite' : 'none',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: displayColor,
            transition: 'none',
            boxShadow: isActive ? `0 0 12px ${activeColor || color}, 0 0 6px ${activeColor || color}` : 'none',
          }}
        />
      </div>
      {warningText && (
        <span style={{ color: PALETTE.ORANGE, fontSize: '9px', fontWeight: 600 }}>
          {warningText}
        </span>
      )}
    </div>
  );
}

// Warp calculation countdown (shows remaining seconds)
function WarpCalculationCountdown({
  startTime,
  duration,
}: {
  startTime?: number;
  duration?: number;
}) {
  const [remaining, setRemaining] = React.useState(() => {
    if (!startTime || !duration) return 0;
    return Math.max(0, duration - (Date.now() - startTime));
  });

  React.useEffect(() => {
    if (!startTime || !duration) return;

    const interval = setInterval(() => {
      const rem = Math.max(0, duration - (Date.now() - startTime));
      setRemaining(rem);
      if (rem <= 0) clearInterval(interval);
    }, 100);

    return () => clearInterval(interval);
  }, [startTime, duration]);

  return (
    <span style={{ color: PALETTE.LIGHT, fontSize: '10px', fontWeight: 500 }}>
      {Math.ceil(remaining / 1000)}s
    </span>
  );
}

// Warp cooldown display (shows remaining time)
function WarpCooldownDisplay({ cooldownUntil }: { cooldownUntil: number }) {
  const [remaining, setRemaining] = React.useState(() => Math.max(0, cooldownUntil - Date.now()));

  React.useEffect(() => {
    // Initialize remaining immediately
    setRemaining(Math.max(0, cooldownUntil - Date.now()));

    const interval = setInterval(() => {
      const now = Date.now();
      const rem = Math.max(0, cooldownUntil - now);
      setRemaining(rem);
    }, 100);

    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const seconds = Math.ceil(remaining / 1000);

  if (remaining <= 0) {
    return <div style={{ color: PALETTE.DIM, fontSize: '10px' }}>Inactive</div>;
  }

  return (
    <div style={{ color: PALETTE.ORANGE, fontSize: '10px', fontWeight: 500 }}>
      Cooldown: {seconds}s
    </div>
  );
}

// CSS keyframes for threat pulsing and cloak (injected once)
const threatPulseStyleId = 'threat-pulse-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(threatPulseStyleId)) {
  const style = document.createElement('style');
  style.id = threatPulseStyleId;
  style.textContent = `
    @keyframes threatPulseYellow {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes threatPulseRed {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }
    @keyframes warpVignettePulse {
      0%, 100% { opacity: 0.85; }
      50% { opacity: 1; }
    }
    @keyframes warpStreaks {
      0% { background-position: 0 0; }
      100% { background-position: 0 -1400px; }
    }
    @keyframes warpArrivalFade {
      0% { opacity: 0.9; }
      100% { opacity: 0; }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; box-shadow: 0 0 10px #8b5cf6; }
      50% { opacity: 0.8; box-shadow: 0 0 20px #8b5cf6; }
    }
    @keyframes protectedPulse {
      0%, 100% { opacity: 1; box-shadow: 0 0 5px #20a5a6; }
      50% { opacity: 0.7; box-shadow: 0 0 10px #20a5a6; }
    }
    @keyframes shieldDragPulse {
      0%, 100% { opacity: 0.8; }
      50% { opacity: 0.4; }
    }
    @keyframes overloadBarPulse {
      0%, 100% { opacity: 1; filter: brightness(1); }
      50% { opacity: 0.7; filter: brightness(1.5); }
    }
  `;
  document.head.appendChild(style);
}

// Zone kind → display label & color
const ZONE_DISPLAY: Record<string, { label: string; color: string }> = {
  safe_zone: { label: 'Safe Zone', color: '#44ddff' },
  nebula: { label: 'Nebula', color: '#bb99ff' },
  asteroid_field: { label: 'Asteroid Field', color: '#8aaa7a' },
  ice: { label: 'Ice Field', color: '#88ddff' },
  wreckage: { label: 'Wreckage Field', color: '#cd7f32' },
  rich_asteroid: { label: 'Rich Asteroid Field', color: '#ffdd44' },
  settlement_zoi: { label: '⚠ Rogue Influence', color: '#ff8866' },
};

// Game clock: 1:1 with real time, year offset for lore (like EVE Online)
function useGameClock() {
  const [gameDate, setGameDate] = useState('');

  useEffect(() => {
    const compute = () => {
      const now = new Date();
      const gameYear = now.getUTCFullYear() + GAME_CLOCK.YEAR_OFFSET;
      const month = String(now.getUTCMonth() + 1).padStart(2, '0');
      const day = String(now.getUTCDate()).padStart(2, '0');
      setGameDate(`${day}.${month}.${gameYear}`);
    };
    compute();
    // Update every minute — date only changes once per day but keeps clock alive
    const interval = setInterval(compute, 60_000);
    return () => clearInterval(interval);
  }, []);

  return gameDate;
}

// Lightweight FPS counter + ping poll for the status panel.
function useNetStats() {
  const [stats, setStats] = useState({ fps: 0, ping: 0 });

  useEffect(() => {
    let raf = 0;
    let frames = 0;
    let lastSampleTime = performance.now();

    const tick = () => {
      frames++;
      const now = performance.now();
      const elapsed = now - lastSampleTime;
      if (elapsed >= 500) {
        const fps = Math.round((frames * 1000) / elapsed);
        const ping = networkManager.getSmoothedRTT();
        setStats({ fps, ping });
        frames = 0;
        lastSampleTime = now;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return stats;
}

// Constellation info display (for top center)
function ConstellationInfo({
  name,
  tier,
  threatLevel,
  zones,
}: {
  name: string;
  tier: number;
  threatLevel: 'green' | 'yellow' | 'red';
  zones: Array<{ kind: string; name?: string }>;
}) {
  const tierColors = ['#888', PALETTE.TEAL, PALETTE.GREEN, PALETTE.YELLOW, PALETTE.ORANGE];
  const threatColors = {
    green: PALETTE.GREEN,
    yellow: PALETTE.YELLOW,
    red: PALETTE.RED,
  };
  const gameDate = useGameClock();

  // Get animation style based on threat level
  const getThreatAnimation = (): React.CSSProperties => {
    switch (threatLevel) {
      case 'yellow':
        return { animation: 'threatPulseYellow 1s ease-in-out infinite' };
      case 'red':
        return { animation: 'threatPulseRed 0.5s ease-in-out infinite' };
      default:
        return {};
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0px',
        padding: '8px 16px',
        backgroundColor: `${PALETTE.PANEL}cc`,
        borderRadius: '4px',
      }}
    >
      {/* Constellation name + tier */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span
          style={{
            color: threatColors[threatLevel],
            fontWeight: 600,
            fontSize: '14px',
            ...getThreatAnimation(),
          }}
        >
          {name}
        </span>
        <span
          style={{
            color: tierColors[tier] || PALETTE.DIM,
            fontSize: '11px',
            padding: '2px 8px',
            backgroundColor: PALETTE.DARK,
            borderRadius: '3px',
            fontWeight: 600,
          }}
        >
          T{tier}
        </span>
      </div>
      {/* Game date */}
      <span style={{ color: PALETTE.DIM, fontSize: '10px', letterSpacing: '0.5px', marginTop: '2px' }}>
        {gameDate} {GAME_CLOCK.ERA_SUFFIX}
      </span>
      {/* Zone info at the bottom */}
      {zones.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '4px' }}>
          {zones.map((z, i) => {
            const display = ZONE_DISPLAY[z.kind] || { label: z.kind, color: PALETTE.DIM };
            const label = z.name ? `${z.name}` : display.label;
            return (
              <span
                key={i}
                style={{
                  color: display.color,
                  fontSize: '10px',
                  opacity: 0.85,
                }}
              >
                {label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Activity status type
type ActivityStatus = 'idle' | 'moving' | 'mining' | 'combat' | 'docking' | 'warping' | 'stunned';

// View mode type
type ViewMode = 'dogfight' | 'strategic';

// Scrambled text glitch chars
const GLITCH_CHARS = '░▒▓█▄▀■□◆◇●○╳╋╬╪';

/** Hook: returns garbled version of `text` that cycles every ~100ms while `active` is true. */
function useScrambledText(text: string, active: boolean): string {
  const [scrambled, setScrambled] = useState(text);
  useEffect(() => {
    if (!active) { setScrambled(text); return; }
    const tick = () => {
      setScrambled(
        text.split('').map(ch => {
          if (ch === ' ') return ' ';
          return Math.random() < 0.6
            ? GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]
            : ch;
        }).join('')
      );
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [text, active]);
  return scrambled;
}

// Activity status config
const activityConfig: Record<ActivityStatus, { label: string; color: string }> = {
  idle: { label: 'IDLE', color: PALETTE.DIM },
  moving: { label: 'MOVING', color: PALETTE.TEAL },
  mining: { label: 'MINING', color: PALETTE.YELLOW },
  combat: { label: 'COMBAT', color: PALETTE.RED },
  docking: { label: 'DOCKING', color: PALETTE.GREEN },
  warping: { label: 'WARPING', color: '#8b5cf6' },
  stunned: { label: 'STUNNED', color: '#ff6b00' },  // Orange - collision stun
};

// Unified Player Status Panel - all player info in one panel
function PlayerStatusPanel({
  shipName,
  shipClassType,
  shipTier,
  totalPower,
  x,
  y,
  speed,
  zoom,
  activityStatus,
  reputation,
  reputationStatus,
  isProtected,
  threatLevel,
  viewMode,
  onViewModeChange,
  // Approach props
  fastTravel,
  onStopFastTravel,
  // Follow props
  followState,
  onStopFollow,
  followDistance,
  followDistanceMin,
  followDistanceMax,
  onChangeFollowDistance,
  // Warp props
  warpState,
  onWarpEngage,
  onWarpCancel,
  // Scanner props
  scannerState,
  // Nebula debuff
  isInNebula,
  // Mining props
  miningToolInfo,
  onStopAllMining,
}: {
  shipName: string;
  shipClassType: string;
  shipTier: number;
  totalPower: number;
  x: number;
  y: number;
  speed: number;
  zoom: number;
  activityStatus: ActivityStatus;
  reputation: number;
  reputationStatus: ReputationStatus;
  isProtected: boolean;
  threatLevel: 'green' | 'yellow' | 'red';
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  fastTravel: {
    isActive: boolean;
    targetName?: string;
    distanceRemaining?: number;
    estimatedTimeSeconds?: number;
  };
  onStopFastTravel: () => void;
  followState: {
    isActive: boolean;
    targetId?: string;
    targetName?: string;
    distanceRemaining?: number;
    followMode?: 'chasing' | 'idle' | 'retreating';
  };
  onStopFollow: () => void;
  followDistance: number;
  followDistanceMin: number;
  followDistanceMax: number;
  onChangeFollowDistance: (value: number) => void;
  warpState: {
    phase: 'none' | 'ready' | 'calculating' | 'traveling' | 'cooldown';
    targetX?: number;
    targetY?: number;
    cooldownUntil?: number;
    calculationStartTime?: number;
    calculationDuration?: number;
    distance?: number;
    estimatedTimeSeconds?: number;
    warmingUpProgress?: number;
    warmingUpRemainingMs?: number;
  };
  onWarpEngage: () => void;
  onWarpCancel: () => void;
  scannerState: {
    hasScanner: boolean;
    isScanning: boolean;
    currentRange: number;
    maxRange: number;
    scanProgress: number;
    scanComplete: boolean;
    isCharging: boolean;
    chargeRemaining: number;
  };
  isInNebula: boolean;
  miningToolInfo: { toolRange: number; toolTier: number; isMining: boolean; miningAsteroidId: string | null; miningTargetIds?: string[]; miningTargetCount?: number; sessionResources?: Record<string, number>; sessionStartTime?: number };
  onStopAllMining: () => void;
}) {
  const activity = activityConfig[activityStatus];

  // Nebula scrambled text for debuffed panels
  const warpScramble = useScrambledText('NEBULA INTERFERENCE', isInNebula);
  const scannerScramble = useScrambledText('SIGNAL LOST', isInNebula);

  // Reputation color based on status
  const getRepColor = () => {
    switch (reputationStatus) {
      case 'outlaw': return '#aa0000';
      case 'wanted': return PALETTE.RED;
      case 'hero': return PALETTE.GREEN;
      default: return PALETTE.LIGHT;
    }
  };

  // Reputation badge
  const getRepBadge = () => {
    switch (reputationStatus) {
      case 'outlaw': return 'OUTLAW';
      case 'wanted': return 'WANTED';
      case 'hero': return 'HERO';
      default: return null;
    }
  };

  const repColor = getRepColor();
  const repBadge = getRepBadge();

  const { fps, ping } = useNetStats();
  const pingColor = ping === 0
    ? PALETTE.DIM
    : ping < 80
    ? PALETTE.GREEN
    : ping < 160
    ? PALETTE.YELLOW
    : PALETTE.RED;
  const fpsColor = fps >= 55 ? PALETTE.GREEN : fps >= 30 ? PALETTE.YELLOW : PALETTE.RED;

  const isWarping = warpState.phase === 'calculating' || warpState.phase === 'traveling';

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '8px 12px',
        backgroundColor: `${PALETTE.PANEL}cc`,
        borderRadius: '4px',
        minWidth: '160px',
        transform: 'scale(1.3)',
        transformOrigin: 'top left',
        fontFamily: FONT_DATA,
      }}
    >
      {/* Row 1: Ship name, tier, power */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', fontFamily: FONT_DISPLAY }}>
        <span style={{ color: PALETTE.LIGHT, fontWeight: 600 }}>{shipName}</span>
        <span
          style={{
            color: getTierColor(shipTier),
            fontSize: '8px',
            padding: '1px 4px',
            backgroundColor: PALETTE.DARK,
            borderRadius: '2px',
          }}
        >
          T{shipTier}
        </span>
        <span style={{ color: PALETTE.ORANGE, fontWeight: 600, marginLeft: 'auto' }}>
          AIP {totalPower}
        </span>
      </div>

      {/* Row 1.5: Protected Status (if applicable) */}
      {isProtected && threatLevel !== 'red' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px' }}>
          <span style={{ color: PALETTE.DIM, fontSize: '8px' }}>SHIELD</span>
          <span style={{ color: PALETTE.TEAL, fontWeight: 600, fontSize: '8px' }}>
            PROTECTED
          </span>
          <span style={{ color: PALETTE.DIM, fontSize: '8px', marginLeft: 'auto' }}>
            ({totalPower}/50)
          </span>
        </div>
      )}

      {/* Row 2: Position X, Y */}
      <div style={{ display: 'flex', gap: '16px', fontSize: '10px' }}>
        <div>
          <span style={{ color: PALETTE.DIM, fontWeight: 500 }}>X: </span>
          <span style={{ color: PALETTE.TEAL, fontWeight: 500 }}>{Math.round(x)}</span>
        </div>
        <div>
          <span style={{ color: PALETTE.DIM, fontWeight: 500 }}>Y: </span>
          <span style={{ color: PALETTE.TEAL, fontWeight: 500 }}>{Math.round(y)}</span>
        </div>
      </div>

      {/* Row 3: Speed, Zoom */}
      <div style={{ display: 'flex', gap: '16px', fontSize: '10px' }}>
        <div>
          <span style={{ color: PALETTE.DIM, fontWeight: 500 }}>SPD: </span>
          <span style={{ color: PALETTE.ORANGE, fontWeight: 600 }}>{Math.round(speed)}</span>
        </div>
        <div>
          <span style={{ color: PALETTE.DIM, fontWeight: 500 }}>ZOOM: </span>
          <span style={{ color: PALETTE.LIGHT, fontWeight: 500 }}>{zoom.toFixed(2)}x</span>
        </div>
      </div>

      {/* Row 3.5: Ping, FPS */}
      <div style={{ display: 'flex', gap: '16px', fontSize: '10px' }}>
        <div>
          <span style={{ color: PALETTE.DIM, fontWeight: 500 }}>PING: </span>
          <span style={{ color: pingColor, fontWeight: 600 }}>
            {ping === 0 ? '—' : `${ping}ms`}
          </span>
        </div>
        <div>
          <span style={{ color: PALETTE.DIM, fontWeight: 500 }}>FPS: </span>
          <span style={{ color: fpsColor, fontWeight: 600 }}>{fps}</span>
        </div>
      </div>

      {/* Separator */}
      <div style={{ borderTop: `1px solid ${PALETTE.DIM}44`, margin: '4px 0' }} />

      {/* Row 4: Activity Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px' }}>
        <span style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT_DISPLAY }}>STATUS</span>
        <span style={{ color: activity.color, fontWeight: 600, letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>{activity.label}</span>
      </div>

      {/* Row 5: Reputation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px' }}>
        <span style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT_DISPLAY }}>REP</span>
        <span style={{ color: repColor, fontWeight: 600 }}>
          {reputation >= 0 ? `+${reputation}` : reputation}
        </span>
        {repBadge && (
          <span
            style={{
              color: repColor,
              fontSize: '7px',
              fontWeight: 600,
              padding: '1px 3px',
              backgroundColor: `${repColor}22`,
              borderRadius: '2px',
            }}
          >
            {repBadge}
          </span>
        )}
      </div>

      {/* Row 6: View Mode Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px' }}>
        <span style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT_DISPLAY }}>VIEW</span>
        <button
          onClick={() => onViewModeChange(viewMode === 'dogfight' ? 'strategic' : 'dogfight')}
          style={{
            backgroundColor: 'transparent',
            border: `1px solid ${PALETTE.TEAL}66`,
            borderRadius: '3px',
            color: PALETTE.TEAL,
            fontSize: '8px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            padding: '2px 6px',
            cursor: 'pointer',
            pointerEvents: 'auto',
            textTransform: 'uppercase' as const,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = PALETTE.TEAL;
            e.currentTarget.style.color = PALETTE.DARK;
            e.currentTarget.style.borderColor = PALETTE.TEAL;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = PALETTE.TEAL;
            e.currentTarget.style.borderColor = `${PALETTE.TEAL}66`;
          }}
        >
          {viewMode === 'dogfight' ? 'DOGFIGHT' : 'STRATEGIC'}
        </button>
      </div>

      {/* Separator */}
      <div style={{ borderTop: `1px solid ${PALETTE.DIM}44`, margin: '4px 0' }} />

      {/* Travel Sub-panel — adaptive: shows APPROACH by default, swaps to FOLLOW when active.
          Approach and follow are mutually exclusive on the GameScene side, so only one is ever live. */}
      {(() => {
        const followLive = !isWarping && followState.isActive;
        const approachLive = !isWarping && !followLive && fastTravel.isActive;
        const label = followLive ? 'FOLLOW' : 'APPROACH';
        const stateColor = followLive ? PALETTE.TEAL : PALETTE.ORANGE;
        const modeLabel = followLive
          ? (followState.followMode === 'retreating' ? 'BACKING OFF'
            : followState.followMode === 'chasing' ? 'CLOSING'
            : 'HOLDING')
          : 'ENGAGED';
        const targetName = followLive
          ? (followState.targetName || 'Target')
          : (fastTravel.targetName || 'Waypoint');
        const distance = followLive
          ? (followState.distanceRemaining || 0)
          : (fastTravel.distanceRemaining || 0);
        const showEta = approachLive; // follow has no meaningful ETA (target may move)
        const onDisengage = followLive ? onStopFollow : onStopFastTravel;

        return (
          <div>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '1.5px', marginBottom: '4px', fontFamily: FONT_DISPLAY }}>{label}</div>
            {isWarping ? (
              <div style={{ color: PALETTE.DIM, fontSize: '8px' }}>Inactive</div>
            ) : (followLive || approachLive) ? (
              <>
                <div style={{ color: stateColor, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>{modeLabel}</div>
                <div style={{ color: PALETTE.LIGHT, fontSize: '8px', fontWeight: 500, marginTop: '2px' }}>
                  {formatDistance(distance)}
                </div>
                <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 500, marginTop: '1px' }}>
                  {targetName}
                </div>
                {showEta && (
                  <div style={{ color: PALETTE.TEAL, fontSize: '8px', fontWeight: 500, marginTop: '1px', opacity: 0.85 }}>
                    ETA: {formatTime(fastTravel.estimatedTimeSeconds || 0)}
                  </div>
                )}
                {followLive && (
                  <div style={{ marginTop: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ color: PALETTE.DIM, fontSize: '7px', fontWeight: 600, letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>RANGE</span>
                      <span style={{ color: PALETTE.LIGHT, fontSize: '8px', fontWeight: 500 }}>{followDistance}m</span>
                    </div>
                    <input
                      type="range"
                      min={followDistanceMin}
                      max={followDistanceMax}
                      step={25}
                      value={followDistance}
                      onChange={(e) => onChangeFollowDistance(Number(e.target.value))}
                      style={{
                        width: '100%',
                        marginTop: '2px',
                        accentColor: PALETTE.TEAL,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                )}
                <button
                  onClick={onDisengage}
                  style={{
                    marginTop: '6px',
                    width: '100%',
                    padding: '4px 6px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${PALETTE.RED}88`,
                    borderRadius: '3px',
                    color: PALETTE.RED,
                    fontSize: '8px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    textTransform: 'uppercase' as const,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = PALETTE.RED;
                    e.currentTarget.style.color = PALETTE.DARK;
                    e.currentTarget.style.borderColor = PALETTE.RED;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = PALETTE.RED;
                    e.currentTarget.style.borderColor = `${PALETTE.RED}88`;
                  }}
                >
                  DISENGAGE
                </button>
              </>
            ) : (
              <div style={{ color: PALETTE.DIM, fontSize: '10px' }}>Inactive</div>
            )}
          </div>
        );
      })()}

      {/* Separator */}
      <div style={{ borderTop: `1px solid ${PALETTE.DIM}44`, margin: '4px 0' }} />

      {/* Warp Sub-panel */}
      <div>
        <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '1.5px', marginBottom: '4px', fontFamily: FONT_DISPLAY }}>WARP DRIVE</div>
        {/* NEBULA INTERFERENCE - warp disabled */}
        {isInNebula ? (
          <div style={{ color: PALETTE.RED, fontSize: '8px', letterSpacing: '0.5px' }}>
            {warpScramble}
          </div>
        ) : (<>
        {warpState.phase === 'none' && (
          warpState.warmingUpProgress !== undefined && warpState.warmingUpProgress >= 0 ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: PALETTE.TEAL, fontSize: '10px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' as const }}>
                  Preparing
                </span>
                <span style={{ color: PALETTE.LIGHT, fontSize: '10px', fontWeight: 500, opacity: 0.8 }}>
                  {Math.ceil((warpState.warmingUpRemainingMs || 0) / 1000)}s
                </span>
              </div>
              <div style={{ marginTop: '4px' }}>
                <BarLoader color={PALETTE.TEAL} width="100%" height={3} speedMultiplier={0.8} />
              </div>
            </>
          ) : warpState.cooldownUntil && warpState.cooldownUntil > Date.now() ? (
            <WarpCooldownDisplay cooldownUntil={warpState.cooldownUntil} />
          ) : (
            <div style={{ color: PALETTE.DIM, fontSize: '10px' }}>Inactive</div>
          )
        )}
        {/* WARP READY */}
        {warpState.phase === 'ready' && (
          <>
            <div style={{ color: PALETTE.TEAL, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>READY</div>
            <button
              onClick={onWarpEngage}
              style={{
                marginTop: '4px',
                width: '100%',
                padding: '5px 6px',
                backgroundColor: PALETTE.TEAL,
                border: 'none',
                borderRadius: '3px',
                color: PALETTE.DARK,
                fontSize: '10px',
                               fontWeight: 700,
                letterSpacing: '1px',
                cursor: 'pointer',
                pointerEvents: 'auto',
                textTransform: 'uppercase' as const,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#30c5c6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.TEAL;
              }}
            >
              ENGAGE WARP
            </button>
          </>
        )}
        {/* CALCULATING */}
        {warpState.phase === 'calculating' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: PALETTE.TEAL, fontSize: '10px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' as const }}>
                Calculating
              </span>
              <WarpCalculationCountdown
                startTime={warpState.calculationStartTime}
                duration={warpState.calculationDuration}
              />
            </div>
            <div style={{ marginTop: '4px' }}>
              <BarLoader color={PALETTE.TEAL} width="100%" height={3} speedMultiplier={0.8} />
            </div>
            <button
              onClick={onWarpCancel}
              style={{
                marginTop: '6px',
                width: '100%',
                padding: '4px 6px',
                backgroundColor: 'transparent',
                border: `1px solid ${PALETTE.RED}88`,
                borderRadius: '3px',
                color: PALETTE.RED,
                fontSize: '8px',
                               fontWeight: 600,
                letterSpacing: '0.5px',
                cursor: 'pointer',
                pointerEvents: 'auto',
                textTransform: 'uppercase' as const,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.RED;
                e.currentTarget.style.color = PALETTE.DARK;
                e.currentTarget.style.borderColor = PALETTE.RED;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = PALETTE.RED;
                e.currentTarget.style.borderColor = `${PALETTE.RED}88`;
              }}
            >
              CANCEL
            </button>
          </>
        )}
        {/* TRAVELING */}
        {warpState.phase === 'traveling' && (
          <>
            <div style={{ color: PALETTE.TEAL, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>WARPING</div>
            <div style={{ color: PALETTE.LIGHT, fontSize: '10px', fontWeight: 500, marginTop: '2px' }}>
              {formatDistance(warpState.distance || 0)}
            </div>
            <div style={{ color: PALETTE.TEAL, fontSize: '10px', fontWeight: 500, marginTop: '1px', opacity: 0.85 }}>
              ETA: {formatTime(warpState.estimatedTimeSeconds || 0)}
            </div>
            <button
              onClick={onWarpCancel}
              style={{
                marginTop: '6px',
                width: '100%',
                padding: '4px 6px',
                backgroundColor: 'transparent',
                border: `1px solid ${PALETTE.RED}88`,
                borderRadius: '3px',
                color: PALETTE.RED,
                fontSize: '8px',
                               fontWeight: 600,
                letterSpacing: '0.5px',
                cursor: 'pointer',
                pointerEvents: 'auto',
                textTransform: 'uppercase' as const,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.RED;
                e.currentTarget.style.color = PALETTE.DARK;
                e.currentTarget.style.borderColor = PALETTE.RED;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = PALETTE.RED;
                e.currentTarget.style.borderColor = `${PALETTE.RED}88`;
              }}
            >
              DISENGAGE
            </button>
          </>
        )}
        {/* COOLDOWN */}
        {warpState.phase === 'cooldown' && (
          <WarpCooldownDisplay cooldownUntil={warpState.cooldownUntil || 0} />
        )}
        </>)}
      </div>

      {/* Mining Sub-panel - shows when mining or session has resources */}
      {(miningToolInfo.isMining || (miningToolInfo.sessionResources && Object.keys(miningToolInfo.sessionResources).length > 0)) && (
        <>
          <div style={{ borderTop: `1px solid ${PALETTE.DIM}44`, margin: '4px 0' }} />
          <div>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '1.5px', marginBottom: '4px', fontFamily: FONT_DISPLAY, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>MINING{miningToolInfo.sessionStartTime ? (() => { const s = Math.floor((Date.now() - miningToolInfo.sessionStartTime!) / 1000); const m = Math.floor(s / 60); return ` ${m}:${String(s % 60).padStart(2, '0')}`; })() : ''}</span>
              {miningToolInfo.isMining && (
                <button
                  onClick={onStopAllMining}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${PALETTE.RED}88`,
                    borderRadius: '3px',
                    color: PALETTE.RED,
                    fontSize: '8px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    padding: '1px 5px',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    textTransform: 'uppercase' as const,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = PALETTE.RED;
                    e.currentTarget.style.color = PALETTE.DARK;
                    e.currentTarget.style.borderColor = PALETTE.RED;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = PALETTE.RED;
                    e.currentTarget.style.borderColor = `${PALETTE.RED}88`;
                  }}
                >
                  STOP ALL
                </button>
              )}
            </div>
            {miningToolInfo.isMining && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: PALETTE.YELLOW, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>
                  {miningToolInfo.miningTargetCount || 1} TARGET{(miningToolInfo.miningTargetCount || 1) > 1 ? 'S' : ''}
                </span>
                <span style={{ color: PALETTE.DIM, fontSize: '10px', fontWeight: 500 }}>
                  T{miningToolInfo.toolTier}
                </span>
              </div>
            )}
            {miningToolInfo.sessionResources && Object.keys(miningToolInfo.sessionResources).length > 0 && (
              <div style={{ marginTop: '4px' }}>
                {Object.entries(miningToolInfo.sessionResources).map(([type, amount]) => (
                  <div key={type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 500, marginTop: '1px' }}>
                    <span style={{ color: PALETTE.DIM, letterSpacing: '0.5px' }}>{type.split('_')[0].toUpperCase()}</span>
                    <span style={{ color: PALETTE.LIGHT }}>+{amount}</span>
                  </div>
                ))}
              </div>
            )}
            {!miningToolInfo.isMining && (
              <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 500, marginTop: '3px', opacity: 0.7 }}>Session expires in 60s</div>
            )}
          </div>
        </>
      )}

      {/* Scanner Sub-panel - only show if player has scanner equipped */}
      {scannerState.hasScanner && (
        <>
          {/* Separator */}
          <div style={{ borderTop: `1px solid ${PALETTE.DIM}44`, margin: '4px 0' }} />

          <div>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 600, letterSpacing: '1.5px', marginBottom: '4px', fontFamily: FONT_DISPLAY }}>SCANNER</div>
            {isInNebula ? (
              <div style={{ color: PALETTE.RED, fontSize: '8px', letterSpacing: '0.5px' }}>
                {scannerScramble}
              </div>
            ) : (scannerState.scanComplete || scannerState.isCharging) ? (
              <>
                <div style={{ color: PALETTE.TEAL, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>
                  SCAN COMPLETE {scannerState.isCharging && `(${Math.ceil(scannerState.chargeRemaining)}s)`}
                </div>
                <div style={{ color: PALETTE.LIGHT, fontSize: '10px', fontWeight: 500, marginTop: '2px' }}>
                  {formatDistance(scannerState.maxRange)}
                </div>
              </>
            ) : scannerState.isScanning ? (
              <>
                <div style={{ color: PALETTE.GREEN, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>SCANNING</div>
                <div style={{ color: PALETTE.LIGHT, fontSize: '10px', fontWeight: 500, marginTop: '2px' }}>
                  {formatDistance(scannerState.currentRange)}
                </div>
              </>
            ) : (
              <>
                <div style={{ color: PALETTE.GREEN, fontWeight: 600, fontSize: '10px', letterSpacing: '1px', fontFamily: FONT_DISPLAY }}>READY</div>
                <div style={{ color: PALETTE.DIM, fontSize: '8px', fontWeight: 500, marginTop: '2px' }}>Hold R longer for more range</div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Controls reference modal
function ControlsModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);
  const controls = [
    // Movement
    { section: 'MOVEMENT' },
    { key: 'W', action: 'Accelerate' },
    { key: 'S', action: 'Reverse' },
    { key: 'A', action: 'Strafe Left' },
    { key: 'D', action: 'Strafe Right' },
    { key: 'Space', action: 'Brake' },
    { key: 'Shift', action: 'Boost' },
    { key: 'Mouse', action: 'Rotate Ship' },
    // Combat
    { section: 'COMBAT' },
    { key: 'Right Click', action: 'Fire Weapon' },
    { key: 'Tab', action: 'Switch Weapon' },
    { key: 'Y', action: 'Inspect Target' },
    { key: 'Q', action: 'Toggle Shield' },
    { key: 'V', action: 'Vent Heat' },
    // Actions
    { section: 'ACTIONS' },
    { key: 'E', action: 'Interact (dock / mine / loot)' },
    { key: 'R', action: 'Radar Scan (hold)' },
    { key: 'F', action: 'Approach (autopilot toward target/waypoint)' },
    { key: 'X', action: 'Follow (continuously track moving target)' },
    { key: 'J', action: 'Use Gateway' },
    { key: 'G', action: 'Mine Selected Target (toggle)' },
    // Utility
    { section: 'UTILITY' },
    { key: '1-5', action: 'Utility Hotbar Slots' },
    // UI Panels
    { section: 'PANELS' },
    { key: 'I', action: 'Inventory' },
    { key: 'O', action: 'Corporation Panel (Organization)' },
    { key: 'N', action: 'Notifications' },
    { key: 'C', action: 'Chat' },
    { key: 'M', action: 'Constellation Map' },
    { key: 'B', action: 'Drone Bay / Bounty Board' },
    { key: 'Esc', action: 'Menu / Close Panel' },
    // Camera
    { section: 'CAMERA' },
    { key: 'Right Click + Drag', action: 'Pan Camera' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(15, 27, 38, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 300,
        fontFamily: FONT_DISPLAY,
        pointerEvents: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: PALETTE.PANEL,
          border: `1px solid ${PALETTE.DIM}66`,
          borderRadius: '6px',
          padding: '20px 28px',
          width: '900px',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ color: PALETTE.TEAL, fontSize: '10px', fontWeight: 600 }}>CONTROLS</span>
          <span
            style={{ color: PALETTE.DIM, fontSize: '9px', cursor: 'pointer' }}
            onClick={onClose}
          >
            ESC to close
          </span>
        </div>
        {controls.map((item, i) =>
          'section' in item && item.section ? (
            <div
              key={i}
              style={{
                color: PALETTE.ORANGE,
                fontSize: '10px',
                fontWeight: 600,
                padding: '8px 0 4px',
                borderBottom: `1px solid ${PALETTE.DIM}33`,
                marginBottom: '4px',
                ...(i > 0 ? { marginTop: '6px' } : {}),
              }}
            >
              {item.section}
            </div>
          ) : (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              <span style={{ color: PALETTE.LIGHT, fontSize: '10px' }}>
                {'action' in item ? item.action : ''}
              </span>
              <span
                style={{
                  color: PALETTE.TEAL,
                  fontSize: '9px',
                  backgroundColor: PALETTE.DARK,
                  padding: '2px 8px',
                  borderRadius: '3px',
                  border: `1px solid ${PALETTE.DIM}44`,
                  minWidth: '50px',
                  textAlign: 'center',
                }}
              >
                {'key' in item ? item.key : ''}
              </span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

// Entity type icons/colors - full type names
const entityTypeConfig: Record<string, { color: string; label: string }> = {
  player: { color: PALETTE.TEAL, label: 'PLAYER' },
  station: { color: PALETTE.GREEN, label: 'STATION' },
  gateway: { color: '#00dddd', label: 'GATEWAY' },
  waypoint: { color: PALETTE.ORANGE, label: 'WAYPOINT' },
  asteroid: { color: PALETTE.YELLOW, label: 'ASTEROID' },
  drop: { color: PALETTE.YELLOW, label: 'LOOT' },
  // Environmental entities (visible when nearby)
  planet: { color: '#8844ff', label: 'PLANET' },
  sun: { color: '#ffcc00', label: 'STAR' },
  nebula: { color: '#ff66cc', label: 'NEBULA' },
  // Scanned entity types (from radar - extended range)
  hotspot: { color: '#ff8800', label: 'ASTEROIDS' },  // Asteroid field hotspot
  drone: { color: '#66ccff', label: 'DRONE' },
  // V1 harvestable fields
  ice_asteroid: { color: '#88ddff', label: 'ICE' },
  gas_pocket:   { color: '#bb99ff', label: 'GAS' },
  wreckage:     { color: '#cd7f32', label: 'WRECK' },
  // Additional scanned types
  private_station: { color: '#22d3ee', label: 'OUTPOST' },
  beacon:          { color: '#ff4466', label: 'BEACON' },
};

// Single entity row in overview - TYPE | NAME | DISTANCE (fixed order)
function EntityRow({
  entity,
  isSelected,
  onClick,
}: {
  entity: NearbyEntity;
  isSelected: boolean;
  onClick: () => void;
}) {
  const config = entityTypeConfig[entity.type] || { color: PALETTE.DIM, label: '???' };

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        backgroundColor: isSelected ? `${PALETTE.TEAL}22` : 'transparent',
        borderLeft: isSelected ? `2px solid ${PALETTE.TEAL}` : '2px solid transparent',
        cursor: 'pointer',
        transition: 'background-color 0.1s',
        pointerEvents: 'auto',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = `${PALETTE.PANEL}`;
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {/* TYPE first */}
      <span
        style={{
          color: config.color,
          fontSize: '8px',
          fontWeight: 600,
          padding: '1px 3px',
          backgroundColor: `${PALETTE.DARK}cc`,
          borderRadius: '2px',
          minWidth: '48px',
          textAlign: 'center',
          letterSpacing: '0.3px',
        }}
      >
        {config.label}
      </span>
      {/* NAME in middle (with [SCANNED] tag if applicable) */}
      <span
        style={{
          flex: 1,
          color: PALETTE.LIGHT,
          fontSize: '9px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
        }}
      >
        {entity.name}
        {entity.isScanned && (
          <span style={{
            color: PALETTE.GREEN,
            fontSize: '7px',
            fontWeight: 600,
            padding: '0px 2px',
            backgroundColor: `${PALETTE.GREEN}22`,
            borderRadius: '2px',
          }}>
            SCAN
          </span>
        )}
      </span>
      {/* DISTANCE last */}
      <span style={{ color: `${PALETTE.DIM}bb`, fontSize: '8px', minWidth: '40px', textAlign: 'right' }}>
        {entity.distance < 1000
          ? `${Math.round(entity.distance)}m`
          : `${(entity.distance / 1000).toFixed(1)}k`}
      </span>
    </div>
  );
}

// Overview panel showing nearby entities - with minimize/maximize
// Shows: waypoint, stations, players, bots, asteroids (only when selected), and scanned entities
function OverviewPanel({
  entities,
  scannedEntities,
  selectedEntity,
  onSelect,
  isMinimized,
  onToggleMinimize,
  waypoint,
  onApproach,
  onFollow,
  onClearWaypoint,
  isApproaching,
  followState,
  isWarping,
  miningToolInfo,
}: {
  entities: NearbyEntity[];
  scannedEntities: NearbyEntity[];
  selectedEntity: NearbyEntity | null;
  onSelect: (entity: NearbyEntity) => void;
  isMinimized: boolean;
  onToggleMinimize: () => void;
  waypoint: { x: number; y: number; name: string; distance: number } | null;
  onApproach: (entity: NearbyEntity | null) => void;
  onFollow: (entity: NearbyEntity | null) => void;
  onClearWaypoint: () => void;
  isApproaching: boolean;
  followState: { isActive: boolean; targetId?: string; targetName?: string; distanceRemaining?: number };
  isWarping: boolean;
  miningToolInfo: { toolRange: number; toolTier: number; isMining: boolean; miningAsteroidId: string | null; miningTargetIds?: string[]; miningTargetCount?: number; sessionResources?: Record<string, number>; sessionStartTime?: number };
}) {
  const selectedId = selectedEntity?.id || null;
  const [searchFilter, setSearchFilter] = useState('');

  // Draggable position — persisted via zustand
  const defaultPos = { x: window.innerWidth - 280 * 1.3 - 10, y: 10 };
  const { overviewPos, setOverviewPos } = usePanelPositionStore();
  const [pos, setPos] = useState(overviewPos ?? defaultPos);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const lastPos = useRef(pos);
  lastPos.current = pos;

  const onDragStart = (e: React.MouseEvent) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  const resetPos = () => {
    setPos(defaultPos);
    setOverviewPos(null);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const next = { x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y };
      lastPos.current = next;
      setPos(next);
    };
    const onUp = () => {
      if (dragging.current) {
        dragging.current = false;
        setOverviewPos(lastPos.current);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Filter to only show relevant entities
  // Asteroids and drops are only shown when they are currently selected
  const filteredEntities = entities.filter(
    (e) =>
      e.type === 'player' ||
      e.type === 'station' ||
      e.type === 'gateway' ||
      e.type === 'planet' ||
      e.type === 'sun' ||
      e.type === 'nebula' ||
      e.type === 'drone' ||
      (e.type === 'asteroid' && e.id === selectedId) ||
      (e.type === 'ice_asteroid' && e.id === selectedId) ||
      (e.type === 'gas_pocket' && e.id === selectedId) ||
      (e.type === 'wreckage' && e.id === selectedId) ||
      (e.type === 'drop' && e.id === selectedId)
  );

  // Add waypoint as a special entity if it exists
  let allEntities: NearbyEntity[] = waypoint
    ? [
        {
          id: 'waypoint',
          type: 'waypoint' as const,
          name: waypoint.name || 'Waypoint',
          distance: waypoint.distance,
          x: waypoint.x,
          y: waypoint.y,
        },
        ...filteredEntities,
      ]
    : filteredEntities;

  // Add scanned entities (from radar) - they all get isScanned: true
  // Deduplicate by ID - scanned entities override nearby entities (e.g., suns/planets can be both)
  if (scannedEntities.length > 0) {
    const scannedWithFlag = scannedEntities.map((e) => ({
      ...e,
      isScanned: true,
    }));

    // Remove duplicates: if a scanned entity has the same ID as a nearby entity, remove the nearby one
    const scannedIds = new Set(scannedWithFlag.map(e => e.id));
    allEntities = allEntities.filter(e => !scannedIds.has(e.id));

    // Add scanned entities
    allEntities = [...allEntities, ...scannedWithFlag];
  }

  // Apply search filter
  const displayEntities = searchFilter
    ? allEntities.filter((e) => {
        const q = searchFilter.toLowerCase();
        return e.name.toLowerCase().includes(q) || e.type.toLowerCase().includes(q);
      })
    : allEntities;

  // Group entities by type and sort each group by distance (ascending)
  const grouped = displayEntities.reduce(
    (acc, entity) => {
      if (!acc[entity.type]) acc[entity.type] = [];
      acc[entity.type].push(entity);
      return acc;
    },
    {} as Record<string, NearbyEntity[]>
  );

  // Sort each group by distance ascending
  for (const type in grouped) {
    grouped[type].sort((a, b) => a.distance - b.distance);
  }

  // Sort order for types (waypoint first, then gateways, stations, players, asteroids, then scanned types)
  const typeOrder = ['waypoint', 'gateway', 'station', 'private_station', 'player', 'asteroid', 'ice_asteroid', 'gas_pocket', 'wreckage', 'drop', 'planet', 'sun', 'hotspot', 'nebula', 'drone', 'beacon'];

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: `${PALETTE.PANEL}dd`,
    borderRadius: '4px',
    border: `1px solid ${PALETTE.DIM}44`,
    overflow: 'hidden',
    transform: 'scale(1.3)',
    transformOrigin: 'top left',
    pointerEvents: 'auto',
    zIndex: 50,
  };

  return (
    <div style={panelStyle}>
      {/* Custom scrollbar styles for overview panel */}
      <style>{`
        .overview-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .overview-scroll::-webkit-scrollbar-track {
          background: ${PALETTE.DARK};
          border-left: 1px solid ${PALETTE.DIM}33;
        }
        .overview-scroll::-webkit-scrollbar-thumb {
          background: ${PALETTE.DIM}88;
          border-radius: 3px;
        }
        .overview-scroll::-webkit-scrollbar-thumb:hover {
          background: ${PALETTE.TEAL}aa;
        }
        .overview-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${PALETTE.DIM}88 ${PALETTE.DARK};
        }
      `}</style>
      {/* Header — drag handle */}
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px 8px',
          borderBottom: isMinimized ? 'none' : `1px solid ${PALETTE.DIM}33`,
          backgroundColor: PALETTE.DARK,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <span style={{ color: PALETTE.DIM, fontWeight: 600, fontSize: '8px', letterSpacing: '1.5px' }}>
          OVERVIEW
        </span>
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          {/* Reset position button */}
          <button
            onClick={(e) => { e.stopPropagation(); resetPos(); }}
            onMouseDown={(e) => e.stopPropagation()}
            title="Reset position"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${PALETTE.DIM}55`,
              borderRadius: '2px',
              color: PALETTE.DIM,
              cursor: 'pointer',
              width: '18px',
              height: '18px',
              padding: 0,
              fontSize: '10px',
              fontFamily: FONT_DISPLAY,
              lineHeight: '16px',
              textAlign: 'center',
              pointerEvents: 'auto',
            }}
          >
            ⊕
          </button>
          {/* Minimize button */}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMinimize(); }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${PALETTE.DIM}55`,
              borderRadius: '2px',
              color: PALETTE.LIGHT,
              cursor: 'pointer',
              width: '18px',
              height: '18px',
              padding: 0,
              fontSize: '10px',
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              lineHeight: '16px',
              textAlign: 'center',
              pointerEvents: 'auto',
            }}
          >
            {isMinimized ? '+' : '_'}
          </button>
        </div>
      </div>

      {/* Content - only shown when not minimized */}
      {!isMinimized && (
        <>
          {/* Search filter */}
          <div style={{ padding: '3px 6px', borderBottom: `1px solid ${PALETTE.DIM}22` }}>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search..."
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
              }}
              style={{
                width: '100%',
                backgroundColor: `${PALETTE.DARK}`,
                border: `1px solid ${PALETTE.DIM}44`,
                borderRadius: '2px',
                color: PALETTE.LIGHT,
                fontSize: '8px',
                fontFamily: FONT_DISPLAY,
                padding: '3px 6px',
                outline: 'none',
                letterSpacing: '0.5px',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = `${PALETTE.TEAL}88`; }}
              onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = `${PALETTE.DIM}44`; }}
            />
          </div>

          {/* Scrollable entity list - max ~10 visible rows */}
          <div className="overview-scroll" style={{ overflowY: 'auto', maxHeight: '280px' }}>
            {typeOrder.map((type) => {
              const typeEntities = grouped[type];
              if (!typeEntities || typeEntities.length === 0) return null;

              return (
                <div key={type}>
                  {typeEntities.map((entity) => (
                    <EntityRow
                      key={entity.id}
                      entity={entity}
                      isSelected={entity.id === selectedId}
                      onClick={() => onSelect(entity)}
                    />
                  ))}
                </div>
              );
            })}
            {displayEntities.length === 0 && (
              <div style={{ padding: '12px', textAlign: 'center', color: `${PALETTE.DIM}88`, fontSize: '8px' }}>
                {searchFilter ? 'No matches' : 'No nearby objects'}
              </div>
            )}
          </div>

          {/* Selected item section */}
          <SelectedItemSection
            selectedEntity={selectedEntity}
            onApproach={onApproach}
            onFollow={onFollow}
            onClearWaypoint={onClearWaypoint}
            isApproaching={isApproaching}
            followState={followState}
            isWarping={isWarping}
            miningToolInfo={miningToolInfo}
          />
        </>
      )}
    </div>
  );
}

// Selected item section at bottom of overview panel
function SelectedItemSection({
  selectedEntity,
  onApproach,
  onFollow,
  onClearWaypoint,
  isApproaching,
  followState,
  isWarping,
  miningToolInfo,
}: {
  selectedEntity: NearbyEntity | null;
  onApproach: (entity: NearbyEntity | null) => void;
  onFollow: (entity: NearbyEntity | null) => void;
  onClearWaypoint: () => void;
  isApproaching: boolean;
  followState: { isActive: boolean; targetId?: string; targetName?: string; distanceRemaining?: number };
  isWarping: boolean;
  miningToolInfo: { toolRange: number; toolTier: number; isMining: boolean; miningAsteroidId: string | null; miningTargetIds?: string[]; miningTargetCount?: number; sessionResources?: Record<string, number>; sessionStartTime?: number };
}) {
  const isWaypoint = selectedEntity?.type === 'waypoint';

  // Check if selected entity is a minable type within tool range
  const minableTypes = new Set(['asteroid', 'ice_asteroid', 'gas_pocket', 'wreckage']);
  const isMinable = minableTypes.has(selectedEntity?.type || '');
  const hasToolEquipped = miningToolInfo.toolRange > 0;
  const asteroidRadius = (selectedEntity?.data?.radius as number) || 80;
  const inToolRange = isMinable && hasToolEquipped && selectedEntity
    ? (selectedEntity.distance <= miningToolInfo.toolRange + asteroidRadius)
    : false;
  const isMiningThis = miningToolInfo.isMining && (miningToolInfo.miningTargetIds || []).includes(selectedEntity?.id || '');
  // Allow mining even while already mining other targets (server validates multi-target limit)
  const canMine = isMinable && hasToolEquipped && inToolRange && !isMiningThis;

  const actionButtonStyle: React.CSSProperties = {
    padding: '4px 8px',
    backgroundColor: PALETTE.PANEL,
    border: `1px solid ${PALETTE.TEAL}`,
    borderRadius: '2px',
    color: PALETTE.TEAL,
    fontSize: '8px',
    fontWeight: 600,
    fontFamily: FONT_DISPLAY,
    cursor: 'pointer',
    pointerEvents: 'auto',
    transition: 'all 0.15s',
    letterSpacing: '0.3px',
  };

  const clearButtonStyle: React.CSSProperties = {
    ...actionButtonStyle,
    border: `1px solid ${PALETTE.DIM}`,
    color: PALETTE.DIM,
  };

  return (
    <div
      style={{
        borderTop: `1px solid ${PALETTE.DIM}33`,
        padding: '6px 8px',
        backgroundColor: PALETTE.DARK,
        minHeight: '60px',
      }}
    >
      <div style={{ color: `${PALETTE.DIM}88`, fontSize: '7px', marginBottom: '4px', letterSpacing: '0.5px' }}>SELECTED ITEM</div>
      {selectedEntity ? (
        <>
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '10px', marginBottom: '3px' }}>
            {selectedEntity.name}
          </div>
          <div style={{ display: 'flex', gap: '8px', fontSize: '8px', color: PALETTE.DIM, marginBottom: '3px', flexWrap: 'wrap' }}>
            <span>
              Dist: <span style={{ color: PALETTE.TEAL }}>
                {selectedEntity.distance < 1000
                  ? `${Math.round(selectedEntity.distance)}m`
                  : `${(selectedEntity.distance / 1000).toFixed(1)}k`}
              </span>
            </span>
            <span>
              Type: <span style={{ color: entityTypeConfig[selectedEntity.type]?.color || PALETTE.DIM }}>
                {selectedEntity.type.toUpperCase()}
              </span>
            </span>
            {typeof selectedEntity.data?.resourceType === 'string' && (
              <span>
                Mineable: <span style={{ color: PALETTE.YELLOW }}>
                  {selectedEntity.data.resourceType.charAt(0).toUpperCase() + selectedEntity.data.resourceType.slice(1).toLowerCase()}
                </span>
              </span>
            )}
            {selectedEntity.type === 'planet' && typeof selectedEntity.data?.planetType === 'string' && (
              <span>
                Class: <span style={{ color: PALETTE.TEAL }}>
                  {selectedEntity.data.planetType.charAt(0).toUpperCase() + selectedEntity.data.planetType.slice(1)}
                </span>
              </span>
            )}
          </div>
          {/* Ship info for players */}
          {selectedEntity.type === 'player' && selectedEntity.shipName && (
            <div style={{ fontSize: '8px', color: PALETTE.DIM, marginBottom: '4px' }}>
              {/* Ship name, class and tier */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                <span>
                  Ship: <span style={{ color: PALETTE.LIGHT }}>{selectedEntity.shipName}</span>
                </span>
                {selectedEntity.shipClassType && (
                  <span>
                    Class: <span style={{ color: PALETTE.LIGHT }}>
                      {selectedEntity.shipClassType.charAt(0).toUpperCase() + selectedEntity.shipClassType.slice(1)}
                    </span>
                  </span>
                )}
                {selectedEntity.shipTier !== undefined && (
                  <span>
                    Tier: <span style={{ color: getTierColor(selectedEntity.shipTier) }}>T{selectedEntity.shipTier}</span>
                  </span>
                )}
              </div>
              {/* AIP and size */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                {selectedEntity.totalPower !== undefined && (
                  <span>
                    AIP: <span style={{ color: PALETTE.ORANGE }}>{selectedEntity.totalPower}</span>
                  </span>
                )}
                {selectedEntity.shipSize && (
                  <span>
                    Size: <span style={{ color: PALETTE.LIGHT }}>
                      {selectedEntity.shipSize.charAt(0).toUpperCase() + selectedEntity.shipSize.slice(1)}
                    </span>
                  </span>
                )}
              </div>
              {/* Hull and Shield */}
              {selectedEntity.hull !== undefined && selectedEntity.maxHull !== undefined && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>
                    Hull: <span style={{ color: selectedEntity.hull < selectedEntity.maxHull * 0.3 ? PALETTE.RED : PALETTE.GREEN }}>
                      {Math.ceil(selectedEntity.hull)}/{Math.ceil(selectedEntity.maxHull)}
                    </span>
                  </span>
                  {selectedEntity.shield !== undefined && selectedEntity.maxShield !== undefined && selectedEntity.maxShield > 0 && (
                    <span>
                      Shield: <span style={{ color: PALETTE.TEAL }}>
                        {Math.ceil(selectedEntity.shield)}/{Math.ceil(selectedEntity.maxShield)}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {/* Approach button - shown for all entities, disabled when traveling, following, or warping */}
            {(() => {
              const approachDisabled = isApproaching || isWarping || followState.isActive;
              return (
                <button
                  style={approachDisabled
                    ? { ...actionButtonStyle, backgroundColor: PALETTE.TEAL, color: PALETTE.DARK, opacity: 0.6, cursor: 'default' }
                    : actionButtonStyle}
                  onClick={approachDisabled ? undefined : () => onApproach(selectedEntity)}
                  disabled={approachDisabled}
                  onMouseEnter={(e) => {
                    if (!approachDisabled) {
                      e.currentTarget.style.backgroundColor = PALETTE.TEAL;
                      e.currentTarget.style.color = PALETTE.DARK;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!approachDisabled) {
                      e.currentTarget.style.backgroundColor = PALETTE.PANEL;
                      e.currentTarget.style.color = PALETTE.TEAL;
                    }
                  }}
                >
                  {isWarping ? 'WARPING...' : isApproaching ? 'APPROACHING...' : followState.isActive ? 'FOLLOWING' : 'APPROACH'}
                </button>
              );
            })()}
            {/* Follow button — continuously tracks moving targets. Hidden for non-physical types. */}
            {selectedEntity && !isWaypoint && !['hotspot', 'beacon'].includes(selectedEntity.type) && (() => {
              const isFollowingThis = followState.isActive && followState.targetId === selectedEntity.id;
              const followDisabled = isWarping || isApproaching;
              const followLabel = isFollowingThis ? 'STOP FOLLOW' : 'FOLLOW';
              const followColor = isFollowingThis ? PALETTE.ORANGE : PALETTE.TEAL;
              const followButtonStyle: React.CSSProperties = followDisabled
                ? { ...actionButtonStyle, border: `1px solid ${PALETTE.DIM}`, color: PALETTE.DIM, opacity: 0.5, cursor: 'default' }
                : { ...actionButtonStyle, border: `1px solid ${followColor}`, color: followColor };
              return (
                <button
                  style={followButtonStyle}
                  onClick={followDisabled ? undefined : () => onFollow(selectedEntity)}
                  disabled={followDisabled}
                  onMouseEnter={(e) => {
                    if (followDisabled) return;
                    e.currentTarget.style.backgroundColor = followColor;
                    e.currentTarget.style.color = PALETTE.DARK;
                  }}
                  onMouseLeave={(e) => {
                    if (followDisabled) return;
                    e.currentTarget.style.backgroundColor = PALETTE.PANEL;
                    e.currentTarget.style.color = followColor;
                  }}
                >
                  {followLabel}
                </button>
              );
            })()}
            {/* Clear waypoint button - only for waypoints */}
            {isWaypoint && (
              <button
                style={clearButtonStyle}
                onClick={onClearWaypoint}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = PALETTE.ORANGE;
                  e.currentTarget.style.color = PALETTE.ORANGE;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = PALETTE.DIM;
                  e.currentTarget.style.color = PALETTE.DIM;
                }}
              >
                CLEAR
              </button>
            )}
            {/* Mine button — asteroid selected, tool equipped, in range */}
            {canMine && selectedEntity && (
              <button
                style={{ ...actionButtonStyle, border: `1px solid ${PALETTE.ORANGE}`, color: PALETTE.ORANGE }}
                onClick={() => emitGameEvent('requestStartMining', { asteroidId: selectedEntity.id, kind: selectedEntity.type })}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = PALETTE.ORANGE;
                  e.currentTarget.style.color = PALETTE.DARK;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = PALETTE.PANEL;
                  e.currentTarget.style.color = PALETTE.ORANGE;
                }}
              >
                MINE
              </button>
            )}
            {/* Stop mining button */}
            {isMiningThis && (
              <button
                style={{ ...actionButtonStyle, border: `1px solid ${PALETTE.RED}`, color: PALETTE.RED }}
                onClick={() => emitGameEvent('requestStopMining')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = PALETTE.RED;
                  e.currentTarget.style.color = PALETTE.DARK;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = PALETTE.PANEL;
                  e.currentTarget.style.color = PALETTE.RED;
                }}
              >
                STOP MINING
              </button>
            )}
            {/* Out of range indicator for minable entities */}
            {isMinable && hasToolEquipped && !inToolRange && !miningToolInfo.isMining && (
              <button
                style={{ ...actionButtonStyle, border: `1px solid ${PALETTE.DIM}`, color: PALETTE.DIM, cursor: 'default', opacity: 0.6 }}
                onClick={() => emitGameEvent('requestStartMining', { asteroidId: selectedEntity!.id, kind: selectedEntity!.type })}
              >
                OUT OF RANGE
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{ color: PALETTE.LIGHT, fontSize: '9px' }}>Nothing Selected</div>
          <div style={{ color: `${PALETTE.DIM}88`, fontSize: '8px', marginTop: '2px' }}>
            Right-click to select
          </div>
        </>
      )}
    </div>
  );
}

// Inspect panel — detailed scan result for a selected target
function InspectPanel({
  data,
  onClose,
}: {
  data: {
    targetId: string;
    targetName: string;
    shipClass: string;
    shipName: string;
    hull: number;
    maxHull: number;
    reputation: number;
    equipment: Array<{
      slotType: string; slotSize: string;
      itemId: string; itemName: string; tier: number; category: string;
    }>;
    cargo?: {
      capacity: number; used: number;
      items: Array<{ itemType: 'resource' | 'equipment'; itemId: string; quantity: number; volume: number }>;
    };
    entityType?: string;
    resourceType?: string;
    resourceAmount?: number;
    resourceTier?: number;
    miningTimeEstimate?: number;
  };
  onClose: () => void;
}) {
  const hullPct = data.maxHull > 0 ? Math.round((data.hull / data.maxHull) * 100) : 0;
  const hullColor = hullPct < 30 ? PALETTE.RED : hullPct < 60 ? PALETTE.ORANGE : PALETTE.GREEN;

  const repLabel = data.reputation <= -500 ? 'OUTLAW'
    : data.reputation <= -200 ? 'WANTED'
    : data.reputation >= 500 ? 'HERO'
    : 'NEUTRAL';
  const repColor = data.reputation <= -500 ? '#aa0000'
    : data.reputation <= -200 ? PALETTE.RED
    : data.reputation >= 500 ? PALETTE.GREEN
    : PALETTE.DIM;

  const slotTypeColor = (cat: string) => {
    switch (cat) {
      case 'WEAPON': return PALETTE.RED;
      case 'TOOL': return PALETTE.YELLOW;
      case 'ENHANCER': return PALETTE.TEAL;
      case 'UTILITY': return '#a78bfa';
      default: return PALETTE.DIM;
    }
  };

  const cargoPct = data.cargo
    ? data.cargo.capacity > 0 ? Math.round((data.cargo.used / data.cargo.capacity) * 100) : 0
    : null;

  // Draggable position — persisted via zustand
  const defaultPos = { x: window.innerWidth - 300, y: Math.floor(window.innerHeight / 2 - 150) };
  const { inspectPos, setInspectPos } = usePanelPositionStore();
  const [pos, setPos] = useState(inspectPos ?? defaultPos);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const lastPos = useRef(pos);
  lastPos.current = pos;

  const onDragStart = (e: React.MouseEvent) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const next = { x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y };
      lastPos.current = next;
      setPos(next);
    };
    const onUp = () => {
      if (dragging.current) {
        dragging.current = false;
        setInspectPos(lastPos.current);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        width: '260px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: `${PALETTE.PANEL}dd`,
        borderRadius: '4px',
        border: `1px solid ${PALETTE.TEAL}66`,
        overflow: 'hidden',
        pointerEvents: 'auto',
        zIndex: 50,
      }}
    >
      {/* Header — drag handle */}
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
          borderBottom: `1px solid ${PALETTE.DIM}44`,
          backgroundColor: PALETTE.DARK,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        <span style={{ color: PALETTE.TEAL, fontWeight: 600, fontSize: '8px', letterSpacing: '1.5px' }}>
          INSPECT
        </span>
        <button
          onClick={onClose}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: PALETTE.DIM,
            cursor: 'pointer',
            padding: '0 4px',
            fontSize: '14px',
            fontFamily: FONT_DISPLAY,
          }}
        >
          x
        </button>
      </div>

      {/* Target identity */}
      <div style={{ padding: '6px 10px', borderBottom: `1px solid ${PALETTE.DIM}22` }}>
        <div style={{ color: PALETTE.LIGHT, fontSize: '9px', fontWeight: 600 }}>
          {data.targetName}
        </div>
        <div style={{ color: PALETTE.DIM, fontSize: '9px', marginTop: '2px' }}>
          {data.shipName}
        </div>
      </div>

      {data.entityType === 'asteroid' ? (
        <>
          {/* Resource info */}
          <div style={{ padding: '5px 10px', borderBottom: `1px solid ${PALETTE.DIM}22` }}>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', marginBottom: '3px' }}>RESOURCE</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
              <span style={{ color: PALETTE.LIGHT }}>{data.resourceType ?? 'Unknown'}</span>
              <span style={{ color: PALETTE.DIM }}>T{data.resourceTier ?? '?'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', marginTop: '2px' }}>
              <span style={{ color: PALETTE.DIM }}>Yield</span>
              <span style={{ color: PALETTE.YELLOW, fontWeight: 600 }}>{data.resourceAmount ?? 0} units</span>
            </div>
          </div>

          {/* Health bar */}
          <div style={{ padding: '5px 10px', borderBottom: `1px solid ${PALETTE.DIM}22` }}>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', marginBottom: '2px' }}>HEALTH</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                flex: 1, height: '4px', backgroundColor: `${PALETTE.DIM}33`, borderRadius: '2px',
              }}>
                <div style={{
                  width: `${hullPct}%`, height: '100%', backgroundColor: hullColor,
                  borderRadius: '2px', transition: 'width 0.3s',
                }} />
              </div>
              <span style={{ color: PALETTE.DIM, fontSize: '9px', minWidth: '55px', textAlign: 'right' }}>
                {data.hull}/{data.maxHull}
              </span>
            </div>
          </div>

          {/* Mining time estimate */}
          <div style={{ padding: '5px 10px' }}>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', marginBottom: '3px' }}>MINING</div>
            {data.miningTimeEstimate != null ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
                <span style={{ color: PALETTE.DIM }}>Est. time</span>
                <span style={{ color: PALETTE.TEAL, fontWeight: 600 }}>
                  {data.miningTimeEstimate >= 60
                    ? `${Math.floor(data.miningTimeEstimate / 60)}m ${data.miningTimeEstimate % 60}s`
                    : `${data.miningTimeEstimate}s`}
                </span>
              </div>
            ) : (
              <div style={{ color: PALETTE.DIM, fontSize: '9px', fontStyle: 'italic' }}>No mining tool equipped</div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Hull + Reputation row */}
          <div style={{ display: 'flex', gap: '8px', padding: '5px 10px', borderBottom: `1px solid ${PALETTE.DIM}22` }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: PALETTE.DIM, fontSize: '8px', marginBottom: '2px' }}>HULL</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  flex: 1, height: '4px', backgroundColor: `${PALETTE.DIM}33`, borderRadius: '2px',
                }}>
                  <div style={{
                    width: `${hullPct}%`, height: '100%', backgroundColor: hullColor,
                    borderRadius: '2px', transition: 'width 0.3s',
                  }} />
                </div>
                <span style={{ color: hullColor, fontSize: '9px', minWidth: '30px', textAlign: 'right' }}>
                  {hullPct}%
                </span>
              </div>
            </div>
            <div>
              <div style={{ color: PALETTE.DIM, fontSize: '8px', marginBottom: '2px' }}>REP</div>
              <span style={{ color: repColor, fontSize: '9px', fontWeight: 600 }}>{repLabel}</span>
            </div>
          </div>

          {/* Equipment */}
          <div style={{ padding: '5px 10px', borderBottom: `1px solid ${PALETTE.DIM}22` }}>
            <div style={{ color: PALETTE.DIM, fontSize: '8px', marginBottom: '3px' }}>EQUIPMENT</div>
            {data.equipment.length === 0 ? (
              <div style={{ color: PALETTE.DIM, fontSize: '9px', fontStyle: 'italic' }}>No equipment</div>
            ) : (
              data.equipment.map((eq, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '2px 0', fontSize: '9px',
                }}>
                  <span style={{
                    color: slotTypeColor(eq.category), fontSize: '7px',
                    minWidth: '10px', textAlign: 'center', fontWeight: 600,
                  }}>
                    {eq.category === 'WEAPON' ? 'W' : eq.category === 'TOOL' ? 'T' : eq.category === 'ENHANCER' ? 'E' : 'U'}
                  </span>
                  <span style={{ color: getTierColor(eq.tier), flex: 1 }}>
                    {eq.itemName}
                  </span>
                  <span style={{ color: PALETTE.DIM, fontSize: '8px' }}>
                    {eq.slotSize}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Cargo */}
          {data.cargo && (
            <div style={{ padding: '5px 10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                <span style={{ color: PALETTE.DIM, fontSize: '8px' }}>CARGO</span>
                <span style={{ color: PALETTE.DIM, fontSize: '8px' }}>
                  {data.cargo.used}/{data.cargo.capacity} ({cargoPct}%)
                </span>
              </div>
              {data.cargo.items.length === 0 ? (
                <div style={{ color: PALETTE.DIM, fontSize: '9px', fontStyle: 'italic' }}>Empty</div>
              ) : (
                data.cargo.items.slice(0, 8).map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '1px 0', fontSize: '9px',
                  }}>
                    <span style={{ color: PALETTE.LIGHT }}>
                      {item.itemId.replace(/_/g, ' ')}
                    </span>
                    <span style={{ color: PALETTE.DIM }}>
                      x{item.quantity}
                    </span>
                  </div>
                ))
              )}
              {data.cargo.items.length > 8 && (
                <div style={{ color: PALETTE.DIM, fontSize: '8px', textAlign: 'center', marginTop: '2px' }}>
                  +{data.cargo.items.length - 8} more
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Main HUD component
export function GameHUD() {
  // Player state
  const [hull, setHull] = useState(100);
  const [maxHull, setMaxHull] = useState(100);
  const [shield, setShield] = useState(50);
  const [maxShield, setMaxShield] = useState(50);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [totalPower, setTotalPower] = useState(0);

  // Weapon heat state
  const [weaponHeat, setWeaponHeat] = useState(0);
  const [weaponMaxHeat, setWeaponMaxHeat] = useState(100);
  const [isOverheated, setIsOverheated] = useState(false);
  const [hasWeapon, setHasWeapon] = useState(false);

  // Missile ammo state
  const [ammoCount, setAmmoCount] = useState(0);
  const [magazineSize, setMagazineSize] = useState(0);
  const [isReloading, setIsReloading] = useState(false);
  const [reloadProgress, setReloadProgress] = useState(0);
  const [isMissileWeapon, setIsMissileWeapon] = useState(false);

  // Boost state
  const [boostEnergy, setBoostEnergy] = useState(100);
  const [boostMaxEnergy, setBoostMaxEnergy] = useState(100);
  const [isBoosting, setIsBoosting] = useState(false);
  const [hasBooster, setHasBooster] = useState(false);

  // Shield drag state
  const [isShieldDraining, setIsShieldDraining] = useState(false);
  // Combat-overhaul: ship-level heat & natural shield
  const [shipHeat, setShipHeat] = useState(0);
  const [shipMaxHeat, setShipMaxHeat] = useState(100);
  const [isShipOverheated, setIsShipOverheated] = useState(false);
  const [shieldActive, setShieldActive] = useState(false);
  const [isVenting, setIsVenting] = useState(false);
  const [powerCapacity, setPowerCapacity] = useState(100);

  // Active weapon info
  const [activeWeaponName, setActiveWeaponName] = useState<string | null>(null);
  const [activeWeaponType, setActiveWeaponType] = useState<string | null>(null);
  const [activeWeaponIndex, setActiveWeaponIndex] = useState(0);
  const [equippedWeaponCount, setEquippedWeaponCount] = useState(0);

  // Ship info state
  const [shipName, setShipName] = useState('Sparrow');
  const [shipClassType, setShipClassType] = useState('corvette');
  const [shipTier, setShipTier] = useState(1);

  // Activity status state
  const [activityStatus, setActivityStatus] = useState<ActivityStatus>('idle');

  // View mode state (persisted to localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('astra_view_mode');
    return (saved === 'strategic' ? 'strategic' : 'dogfight') as ViewMode;
  });

  // Controls modal
  const [showControls, setShowControls] = useState(false);

  // Constellation state
  const [constellationName, setConstellationName] = useState('Unknown Constellation');
  const [constellationTier, setConstellationTier] = useState(1);
  const [threatLevel, setThreatLevel] = useState<'green' | 'yellow' | 'red'>('green');
  const [playerZones, setPlayerZones] = useState<Array<{ kind: string; name?: string }>>([]);

  // Reputation state
  const [reputation, setReputation] = useState(0);
  const [reputationStatus, setReputationStatus] = useState<ReputationStatus>('neutral');
  const [policeWarning, setPoliceWarning] = useState<string | null>(null);
  // Nebula debuff state
  const [isInNebula, setIsInNebula] = useState(false);

  // Radiation warning from suns (proximity 0-1, 0 = safe, 1 = lethal)
  const [radiationWarning, setRadiationWarning] = useState<{ proximity: number; damage: number } | null>(null);
  const radiationWarningTimeout = useRef<NodeJS.Timeout | null>(null);

  // Overview state
  const [nearbyEntities, setNearbyEntities] = useState<NearbyEntity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<NearbyEntity | null>(null);
  const [overviewMinimized, setOverviewMinimized] = useState(false);

  // Dock prompt state
  const [dockPrompt, setDockPrompt] = useState<{ show: boolean; stationName?: string }>({ show: false });

  // Mining prompt state
  const [miningPrompt, setMiningPrompt] = useState<{ show: boolean; resourceType?: string; tierAdvantage?: number; resourceTier?: number; equippedTier?: number }>({ show: false });

  // Mining tool info (for Mine button in overview panel)
  const [miningToolInfo, setMiningToolInfo] = useState<{ toolRange: number; toolTier: number; isMining: boolean; miningAsteroidId: string | null; miningTargetIds?: string[]; miningTargetCount?: number; sessionResources?: Record<string, number>; sessionStartTime?: number }>({ toolRange: 0, toolTier: 0, isMining: false, miningAsteroidId: null, sessionStartTime: 0 });

  // Gateway prompt state
  const [gatewayPrompt, setGatewayPrompt] = useState<{ show: boolean; targetConstellationName?: string; travelCost?: number; gatewayId?: string }>({ show: false });

  // Planet landing prompt state
  const [planetLandingPrompt, setPlanetLandingPrompt] = useState<{ show: boolean; planetName?: string; planetId?: string }>({ show: false });

  // Drop pickup prompt state
  const [dropPrompt, setDropPrompt] = useState<{ show: boolean; dropId?: string; ownerOnly?: boolean; canAccess?: boolean }>({ show: false });

  // Loot modal state
  const [lootModal, setLootModal] = useState<{ dropId: string; items: Array<{ itemType: 'credits' | 'resource' | 'equipment'; itemId: string; quantity: number }> } | null>(null);

  // Cargo scan result popup
  const [cargoScanResult, setCargoScanResult] = useState<{
    targetName: string;
    shipClass: string;
    cargo: { capacity: number; used: number; items: Array<{ itemType: string; itemId: string; quantity: number }> };
  } | null>(null);

  // Ship drone bay overlay (toggled with [B] in flight)
  const [shipBayOpen, setShipBayOpen] = useState(false);

  // Gateway transition overlay state
  const [gatewayTransition, setGatewayTransition] = useState<{ show: boolean; targetConstellationName?: string }>({ show: false });

  // Planet transition overlay state (landing/takeoff)

  // Waypoint state
  const [waypoint, setWaypoint] = useState<{
    x: number;
    y: number;
    name: string;
    distance: number;
    angle: number;
    isOnScreen: boolean;
  } | null>(null);

  // Fast travel state
  const [fastTravel, setFastTravel] = useState<{
    isActive: boolean;
    targetName?: string;
    distanceRemaining?: number;
    estimatedTimeSeconds?: number;
  }>({ isActive: false });

  // Follow state
  const [followState, setFollowState] = useState<{
    isActive: boolean;
    targetId?: string;
    targetName?: string;
    distanceRemaining?: number;
    followMode?: 'chasing' | 'idle' | 'retreating';
  }>({ isActive: false });

  // User-controlled clearance distance for follow (persisted between sessions)
  const FOLLOW_DISTANCE_MIN = 50;
  const FOLLOW_DISTANCE_MAX = 1500;
  const [followDistance, setFollowDistanceState] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('astra_follow_distance') : null;
    const parsed = saved ? Number(saved) : NaN;
    return Number.isFinite(parsed) ? Math.max(FOLLOW_DISTANCE_MIN, Math.min(FOLLOW_DISTANCE_MAX, parsed)) : 300;
  });
  const setFollowDistance = (next: number) => {
    const clamped = Math.max(FOLLOW_DISTANCE_MIN, Math.min(FOLLOW_DISTANCE_MAX, Math.round(next)));
    setFollowDistanceState(clamped);
    localStorage.setItem('astra_follow_distance', String(clamped));
    emitGameEvent('setFollowDistance', { distance: clamped });
  };

  // Warp travel state
  const [warpState, setWarpState] = useState<{
    phase: 'none' | 'ready' | 'calculating' | 'traveling' | 'cooldown';
    targetX?: number;
    targetY?: number;
    cooldownUntil?: number;
    calculationStartTime?: number;
    calculationDuration?: number;
    distance?: number;
    estimatedTimeSeconds?: number;
    warmingUpProgress?: number;  // 0-1 progress toward ready, -1 = not warming up
    warmingUpRemainingMs?: number;  // Ms until warp ready
  }>({ phase: 'none' });

  // Warp arrival flash: a short fade that masks the destination entity pop-in.
  // The server freezes the AOI during warp TRAVELING and reloads it in one batch
  // on exit (see ConstellationRoom.updateAllClientViews / exitWarp), so without
  // this the destination surroundings would visibly snap in.
  const [warpArrivalFlash, setWarpArrivalFlash] = useState(false);
  const prevWarpPhaseRef = useRef<string>('none');

  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
  } | null>(null);

  // Gateway cloak state
  const [cloakState, setCloakState] = useState<{
    isCloaked: boolean;
    expiresAt: number;
    remaining: number;
  }>({ isCloaked: false, expiresAt: 0, remaining: 0 });

  // Combat state (from lastCombatTime synced from server)
  const [combatState, setCombatState] = useState<{
    isInCombat: boolean;
    expiresAt: number;
    remaining: number;
  }>({ isInCombat: false, expiresAt: 0, remaining: 0 });

  // Radar scan state
  const [radarState, setRadarState] = useState<{
    isScanning: boolean;
    hasRadar: boolean;
    currentRange: number;
    maxRange: number;
    scanProgress: number;
    scanComplete: boolean;
    isCharging: boolean;
    chargeRemaining: number;
  }>({ isScanning: false, hasRadar: false, currentRange: 0, maxRange: 0, scanProgress: 0, scanComplete: false, isCharging: false, chargeRemaining: 0 });

  // Radar discovered entities
  const [radarEntities, setRadarEntities] = useState<Array<{
    type: NearbyEntity['type'];
    id: string;
    name: string;
    x: number;
    y: number;
    distance: number;
    radius?: number;
    resourceType?: string;
    droneType?: string;
  }>>([]);

  // Inspect target result (detailed scan of selected player)
  const [inspectData, setInspectData] = useState<{
    targetId: string;
    targetName: string;
    shipClass: string;
    shipName: string;
    hull: number;
    maxHull: number;
    reputation: number;
    equipment: Array<{
      slotType: string; slotSize: string;
      itemId: string; itemName: string; tier: number; category: string;
    }>;
    cargo?: {
      capacity: number; used: number;
      items: Array<{ itemType: 'resource' | 'equipment'; itemId: string; quantity: number; volume: number }>;
    };
    entityType?: string;
    resourceType?: string;
    resourceAmount?: number;
    resourceTier?: number;
    miningTimeEstimate?: number;
  } | null>(null);

  // The range at which entities were scanned - used to filter them as player moves
  const [scannedRange, setScannedRange] = useState<number>(0);

  // Refs to track scanning state across effect re-runs (prevents race condition)
  const wasScanningRef = useRef(false);
  const lastKnownRangeRef = useRef(0);

  // Subscribe to game events
  useEffect(() => {
    const unsubPlayerState = onGameEvent('playerStateUpdate', (state) => {
      setHull(state.hull);
      setMaxHull(state.maxHull);
      setShield(state.shield);
      setMaxShield(state.maxShield);
      setX(state.x);
      setY(state.y);
      setRotation(state.rotation);
      setSpeed(state.speed);
      setZoom(state.zoom);
      // Weapon heat
      setWeaponHeat(state.weaponHeat);
      setWeaponMaxHeat(state.weaponMaxHeat);
      setIsOverheated(state.isOverheated);
      setHasWeapon(state.hasWeapon);
      // Missile ammo
      setAmmoCount(state.ammoCount);
      setMagazineSize(state.magazineSize);
      setIsReloading(state.isReloading);
      setReloadProgress(state.reloadProgress);
      setIsMissileWeapon(state.isMissileWeapon);
      // Boost
      setBoostEnergy(state.boostEnergy);
      setBoostMaxEnergy(state.boostMaxEnergy);
      setIsBoosting(state.isBoosting);
      setHasBooster(state.hasBooster);
      // Power
      if (state.totalPower !== undefined) {
        setTotalPower(state.totalPower);
      }
      // Ship info
      setShipName(state.shipName);
      setShipClassType(state.shipClassType);
      setShipTier(state.shipTier);
      // Activity status
      setActivityStatus(state.activityStatus);
      // Shield drag
      setIsShieldDraining(state.isShieldDraining);
      // Combat-overhaul
      if (state.shipHeat !== undefined) setShipHeat(state.shipHeat);
      if (state.shipMaxHeat !== undefined) setShipMaxHeat(state.shipMaxHeat);
      if (state.isShipOverheated !== undefined) setIsShipOverheated(state.isShipOverheated);
      if (state.shieldActive !== undefined) setShieldActive(state.shieldActive);
      if (state.isVenting !== undefined) setIsVenting(state.isVenting);
      if (state.powerCapacity !== undefined) setPowerCapacity(state.powerCapacity);
      // Active weapon
      setActiveWeaponName(state.activeWeaponName ?? null);
      setActiveWeaponType(state.activeWeaponType ?? null);
      setActiveWeaponIndex(state.activeWeaponIndex ?? 0);
      setEquippedWeaponCount(state.equippedWeaponCount ?? 0);
    });

    const unsubConstellationInfo = onGameEvent('constellationInfo', (info) => {
      setConstellationName(info.name);
      setConstellationTier(info.tier);
      setThreatLevel(info.threatLevel);
    });

    const unsubPlayerZones = onGameEvent('playerZones', (data) => {
      setPlayerZones(data.zones);
    });

    const unsubNearbyEntities = onGameEvent('nearbyEntitiesUpdate', (entities) => {
      setNearbyEntities(entities);
      // Update selected entity if it's still in the list
      if (selectedEntity) {
        const updated = entities.find((e: NearbyEntity) => e.id === selectedEntity.id);
        if (updated) {
          setSelectedEntity(updated);
        }
      }
    });

    const unsubDockPrompt = onGameEvent('dockPrompt', (data) => {
      setDockPrompt(data);
    });

    const unsubMiningPrompt = onGameEvent('miningPrompt', (data) => {
      setMiningPrompt(data);
    });

    const unsubMiningToolInfo = onGameEvent('miningToolInfo', (data) => {
      setMiningToolInfo(data);
    });

    const unsubGatewayPrompt = onGameEvent('gatewayPrompt', (data) => {
      setGatewayPrompt(data);
    });

    const unsubPlanetLandingPrompt = onGameEvent('planetLandingPrompt', (data) => {
      setPlanetLandingPrompt(data);
    });

    const unsubDropPrompt = onGameEvent('dropPrompt', (data) => {
      setDropPrompt(data);
    });
    const unsubOpenDropModal = onGameEvent('openDropModal', (data) => {
      setLootModal(data);
    });
    const unsubDropInventoryUpdate = onGameEvent('dropInventoryUpdate', (data) => {
      setLootModal((current) => {
        if (!current || current.dropId !== data.dropId) return current;
        return { ...current, items: data.items };
      });
    });
    const unsubDropRemoved = onGameEvent('dropRemoved', (data) => {
      setLootModal((current) => (current && current.dropId === data.dropId ? null : current));
      // Also remove from radar scanned entities so it disappears from overview panel
      setRadarEntities((prev) => prev.filter((e) => e.id !== data.dropId));
    });
    const unsubToggleShipBay = onGameEvent('toggleShipBay', () => {
      setShipBayOpen((v) => !v);
    });

    const unsubCargoScan = onGameEvent('cargoScanResult', (data) => {
      if (data.success && data.cargo) {
        setCargoScanResult({
          targetName: data.targetName || 'Unknown',
          shipClass: data.shipClass || 'unknown',
          cargo: data.cargo,
        });
        // Auto-dismiss after 8 seconds
        setTimeout(() => setCargoScanResult(null), 8000);
      } else if (data.error) {
        // Show error as notification
        emitGameEvent('notification', { type: 'warning', message: data.error });
      }
    });

    const unsubInspect = onGameEvent('inspectResult', (data) => {
      if (data.success) {
        setInspectData({
          targetId: data.targetId || '',
          targetName: data.targetName || 'Unknown',
          shipClass: data.shipClass || 'unknown',
          shipName: data.shipName || data.shipClass || 'Unknown',
          hull: data.hull ?? 0,
          maxHull: data.maxHull ?? 0,
          reputation: data.reputation ?? 0,
          equipment: data.equipment || [],
          cargo: data.cargo,
          entityType: data.entityType,
          resourceType: data.resourceType,
          resourceAmount: data.resourceAmount,
          resourceTier: data.resourceTier,
          miningTimeEstimate: data.miningTimeEstimate,
        });
      } else if (data.error) {
        emitGameEvent('notification', { type: 'warning', message: data.error });
      }
    });

    const unsubInspectClose = onGameEvent('inspectClose', () => {
      setInspectData(null);
    });

    const unsubGatewayTransition = onGameEvent('gatewayTransition', (data) => {
      setGatewayTransition(data);
    });

    const unsubWaypoint = onGameEvent('waypointUpdate', (data) => {
      setWaypoint(data);
      // Clear selected entity if waypoint was cleared and it was selected
      if (!data) {
        setSelectedEntity(prev => prev?.type === 'waypoint' ? null : prev);
      }
    });

    const unsubFastTravel = onGameEvent('fastTravelUpdate', (data) => {
      setFastTravel(data);
    });

    const unsubFollow = onGameEvent('followUpdate', (data) => {
      setFollowState(data);
    });

    const unsubWarpStateChanged = onGameEvent('warpStateChanged', (data) => {
      // Leaving TRAVELING (arrival / disengage / interdiction) → flash to mask
      // the one-shot destination AOI reload the server fires in exitWarp.
      if (prevWarpPhaseRef.current === 'traveling' && data.phase !== 'traveling') {
        setWarpArrivalFlash(true);
        window.setTimeout(() => setWarpArrivalFlash(false), 450);
      }
      prevWarpPhaseRef.current = data.phase;
      setWarpState(prev => ({
        ...prev,
        phase: data.phase,
        targetX: data.targetX,
        targetY: data.targetY,
        cooldownUntil: data.cooldownUntil,
        calculationStartTime: data.calculationStartTime,
        calculationDuration: data.calculationDuration,
        distance: data.distance,
        estimatedTimeSeconds: data.estimatedTimeSeconds,
      }));
    });

    const unsubWarpReadyChanged = onGameEvent('warpReadyChanged', (data) => {
      if (data.ready) {
        setWarpState(prev => ({
          ...prev,
          phase: 'ready',
          targetX: data.targetX,
          targetY: data.targetY,
          warmingUpProgress: undefined,
          warmingUpRemainingMs: undefined,
        }));
      } else {
        setWarpState(prev => ({
          ...prev,
          phase: prev.phase === 'ready' ? 'none' : prev.phase,
        }));
      }
    });

    const unsubWarpWarmingUp = onGameEvent('warpWarmingUp', (data) => {
      setWarpState(prev => ({
        ...prev,
        warmingUpProgress: data.progress,
        warmingUpRemainingMs: data.remainingMs,
      }));
    });

    const unsubNotification = onGameEvent('notification', (data) => {
      setNotification({ message: data.message, type: data.type });
      // Auto-hide after duration
      setTimeout(() => {
        setNotification(null);
      }, data.duration || 3000);
    });

    // Listen for selection changes from GameScene (right-click in world)
    const unsubSelectionChanged = onGameEvent('selectionChanged', (data) => {
      if (data && data.type !== 'empty') {
        // Find the entity in our nearbyEntities list
        const entity = nearbyEntities.find((e) => e.id === data.id);
        if (entity) {
          setSelectedEntity(entity);
        } else {
          // Entity not in overview - create a temporary one
          setSelectedEntity({
            id: data.id,
            type: data.type as NearbyEntity['type'],
            name: data.name,
            distance: 0,
            x: data.x,
            y: data.y,
          });
        }
      } else {
        setSelectedEntity(null);
      }
      // Clear inspect when selection changes (stale data for old target)
      setInspectData(null);
    });

    // Cloak state changes
    const unsubCloakStateUpdate = onGameEvent('cloakStateUpdate', (data) => {
      setCloakState(data);
    });

    const unsubCloakBroken = onGameEvent('cloakBroken', () => {
      setCloakState({ isCloaked: false, expiresAt: 0, remaining: 0 });
    });

    // Combat state changes
    const unsubCombatStateUpdate = onGameEvent('combatStateUpdate', (data) => {
      setCombatState(data);
    });

    // Reputation sync - initial reputation on join
    const unsubReputationSync = onGameEvent('reputationSync', (data: { reputation: number; status: ReputationStatus }) => {
      setReputation(data.reputation);
      setReputationStatus(data.status);
    });

    // Reputation change - when reputation changes (kill, etc)
    const unsubReputationChange = onGameEvent('reputationChange', (data: { oldReputation: number; newReputation: number; change: number; status: ReputationStatus }) => {
      setReputation(data.newReputation);
      setReputationStatus(data.status);
      // Show notification for reputation change
      const changeText = data.change > 0 ? `+${data.change}` : `${data.change}`;
      setNotification({
        message: `Reputation ${changeText}`,
        type: data.change > 0 ? 'success' : 'warning',
      });
      setTimeout(() => setNotification(null), 3000);
    });

    // Police warning - when police target the player
    const unsubNebulaState = onGameEvent('nebulaStateChange', (data) => {
      setIsInNebula(data.isInNebula);
    });

    const unsubPoliceWarning = onGameEvent('policeWarning', (data: { message: string }) => {
      setPoliceWarning(data.message);
      // Auto-dismiss after 5 seconds
      setTimeout(() => setPoliceWarning(null), 5000);
    });

    // Radiation proximity warning - continuous check for when player approaches sun
    // Uses radiationProximity event (continuous) instead of radiationDamage (only after death)
    const unsubRadiationProximity = onGameEvent('radiationProximity', (data) => {
      if (data && data.proximity > 0) {
        setRadiationWarning({ proximity: data.proximity, damage: data.damage });
        // Clear any existing timeout
        if (radiationWarningTimeout.current) {
          clearTimeout(radiationWarningTimeout.current);
          radiationWarningTimeout.current = null;
        }
      } else {
        // Clear warning when leaving radiation zone
        setRadiationWarning(null);
        if (radiationWarningTimeout.current) {
          clearTimeout(radiationWarningTimeout.current);
          radiationWarningTimeout.current = null;
        }
      }
    });

    // Reputation state update from PlayerState (persists across scene changes)
    const unsubReputationStateUpdate = onGameEvent('reputationStateUpdate', (data: { reputation: number }) => {
      setReputation(data.reputation);
      // Calculate status from reputation value
      if (data.reputation <= -750) {
        setReputationStatus('outlaw');
      } else if (data.reputation <= -500) {
        setReputationStatus('wanted');
      } else if (data.reputation >= 500) {
        setReputationStatus('hero');
      } else {
        setReputationStatus('neutral');
      }
    });

    // Radar scan state updates - uses refs to track state across effect re-runs
    const unsubRadarScanUpdate = onGameEvent('radarScanUpdate', (data) => {
      // Detect when a NEW scan starts - clear previous scan results
      if (data.isScanning && !wasScanningRef.current) {
        setRadarEntities([]);
        setScannedRange(0);
        lastKnownRangeRef.current = 0;
      }

      // Track the range while scanning (for early valid exit)
      if (data.isScanning && data.currentRange > 0) {
        lastKnownRangeRef.current = data.currentRange;
      }

      wasScanningRef.current = data.isScanning;

      setRadarState({
        isScanning: data.isScanning,
        hasRadar: data.hasRadar,
        currentRange: data.currentRange,
        maxRange: data.maxRange,
        scanProgress: data.scanProgress,
        scanComplete: data.scanComplete,
        isCharging: data.isCharging,
        chargeRemaining: data.chargeRemaining,
      });

      // When scan completes at max range, save the range IMMEDIATELY
      if (data.scanComplete && data.currentRange > 0) {
        const scanRange = data.currentRange;
        lastKnownRangeRef.current = scanRange;
        setScannedRange(scanRange);
      }

      // Early valid scan exit - use the last known range IMMEDIATELY
      // This must happen BEFORE entities arrive to prevent race condition
      if (data.validScan && !data.isScanning && data.isCharging && lastKnownRangeRef.current > 0) {
        setScannedRange(lastKnownRangeRef.current);
      }

      // If scan was cancelled early (invalid - didn't reach first threshold), clear entities
      if (!data.isScanning && !data.isCharging && data.validScan === false) {
        setRadarEntities([]);
        setScannedRange(0);
        lastKnownRangeRef.current = 0;
      }
    });

    // Radar entities discovered - store discovered entities
    // The scannedRange should already be set by radarScanUpdate event
    const unsubRadarEntitiesDiscovered = onGameEvent('radarEntitiesDiscovered', (data) => {
      setRadarEntities(data.entities as any);

      // Safety: If scannedRange is somehow still 0, use the max distance from discovered entities
      // This prevents entities from being immediately filtered out
      if (lastKnownRangeRef.current > 0) {
        setScannedRange(lastKnownRangeRef.current);
      }
    });

    // Explicit clear event (death, docking, leaving constellation, etc.)
    const unsubRadarClearEntities = onGameEvent('radarClearEntities', () => {
      setRadarEntities([]);
      setScannedRange(0);
    });

    // Respond to requests for current scanned entities (e.g., when constellation map opens)
    const unsubRequestScanned = onGameEvent('requestScannedEntities', () => {
      emitGameEvent('requestScannedEntitiesResponse', { entities: radarEntities as any });
    });

    return () => {
      unsubPlayerState();
      unsubConstellationInfo();
      unsubPlayerZones();
      unsubNearbyEntities();
      unsubDockPrompt();
      unsubMiningPrompt();
      unsubMiningToolInfo();
      unsubGatewayPrompt();
      unsubPlanetLandingPrompt();
      unsubDropPrompt();
      unsubOpenDropModal();
      unsubDropInventoryUpdate();
      unsubDropRemoved();
      unsubToggleShipBay();
      unsubCargoScan();
      unsubInspect();
      unsubInspectClose();
      unsubGatewayTransition();
      unsubWaypoint();
      unsubFastTravel();
      unsubFollow();
      unsubWarpStateChanged();
      unsubWarpReadyChanged();
      unsubWarpWarmingUp();
      unsubNotification();
      unsubSelectionChanged();
      unsubCloakStateUpdate();
      unsubCloakBroken();
      unsubCombatStateUpdate();
      unsubReputationSync();
      unsubReputationChange();
      unsubNebulaState();
      unsubPoliceWarning();
      unsubRadiationProximity();
      unsubReputationStateUpdate();
      if (radiationWarningTimeout.current) {
        clearTimeout(radiationWarningTimeout.current);
      }
      unsubRadarScanUpdate();
      unsubRadarEntitiesDiscovered();
      unsubRadarClearEntities();
      unsubRequestScanned();
    };
  }, [selectedEntity, nearbyEntities, radarEntities]);

  // Update scanned entities distances and filter by scanned range as player moves
  useEffect(() => {
    if (radarEntities.length === 0 || scannedRange === 0) return;

    // Recalculate distances and filter entities that left the scanned range
    const updatedEntities = radarEntities
      .map(entity => {
        const dx = entity.x - x;
        const dy = entity.y - y;
        const newDistance = Math.sqrt(dx * dx + dy * dy);
        return { ...entity, distance: newDistance };
      })
      .filter(entity => entity.distance <= scannedRange);

    // Only update if something changed (entity left range)
    if (updatedEntities.length !== radarEntities.length) {
      setRadarEntities(updatedEntities);
    } else {
      // Update distances without triggering re-render loop
      const distancesChanged = radarEntities.some((e, i) =>
        Math.abs(e.distance - updatedEntities[i].distance) > 10
      );
      if (distancesChanged) {
        setRadarEntities(updatedEntities);
      }
    }
  }, [x, y, scannedRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle X key for clearing waypoint
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // X key clears waypoint
      if (e.key.toLowerCase() === 'x' && waypoint) {
        emitGameEvent('clearWaypoint');
      }

      // Combat-overhaul: Q toggles natural shield up/down. Hard-flux vent
      // lock: shield can't be raised while venting (server rejects too).
      if (e.key.toLowerCase() === 'q' && !e.repeat) {
        if (shieldActive) networkManager.sendShieldDown();
        else if (!isVenting) networkManager.sendShieldUp();
      }

      // Combat-overhaul: V triggers voluntary vent (2× dissipation, weapons
      // locked until heat=0). Server rejects if already overheated.
      if (e.key.toLowerCase() === 'v' && !e.repeat) {
        networkManager.sendVent();
      }

      // Combat-overhaul: Tab cycles active weapon (for ships with multiple)
      if (e.key === 'Tab' && !e.repeat) {
        e.preventDefault();
        emitGameEvent('cycleActiveWeapon');
      }

      // G toggles mining on the currently selected target (start if not mining,
      // stop only that target if already mining it). Mirrors the Mine/Stop
      // buttons in the overview panel.
      if (e.key.toLowerCase() === 'g' && !e.repeat) {
        if (!selectedEntity) {
          emitGameEvent('notification', { type: 'warning', message: 'Nothing selected', duration: 1500 });
          return;
        }
        const minableTypes = new Set(['asteroid', 'ice_asteroid', 'gas_pocket', 'wreckage']);
        if (!minableTypes.has(selectedEntity.type)) {
          emitGameEvent('notification', { type: 'warning', message: 'Selected target is not minable', duration: 1500 });
          return;
        }
        const isMiningThis = miningToolInfo.isMining && (miningToolInfo.miningTargetIds || []).includes(selectedEntity.id);
        if (isMiningThis) {
          emitGameEvent('requestStopMining', { asteroidId: selectedEntity.id });
        } else {
          emitGameEvent('requestStartMining', { asteroidId: selectedEntity.id, kind: selectedEntity.type });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [waypoint, shieldActive, isVenting, selectedEntity, miningToolInfo]);

  // Update cloak timer every 100ms when cloaked
  useEffect(() => {
    if (!cloakState.isCloaked) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, cloakState.expiresAt - now);
      if (remaining <= 0) {
        setCloakState({ isCloaked: false, expiresAt: 0, remaining: 0 });
      } else {
        setCloakState(prev => ({ ...prev, remaining }));
      }
    }, 100);

    return () => clearInterval(interval);
  }, [cloakState.isCloaked, cloakState.expiresAt]);

  // Update combat timer every 100ms when in combat
  useEffect(() => {
    if (!combatState.isInCombat) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, combatState.expiresAt - now);
      if (remaining <= 0) {
        setCombatState({ isInCombat: false, expiresAt: 0, remaining: 0 });
      } else {
        setCombatState(prev => ({ ...prev, remaining }));
      }
    }, 100);

    return () => clearInterval(interval);
  }, [combatState.isInCombat, combatState.expiresAt]);

  const handleEntitySelect = (entity: NearbyEntity) => {
    const newSelection = entity.id === selectedEntity?.id ? null : entity;
    setSelectedEntity(newSelection);

    // Also tell GameScene to select this entity (if not waypoint, and not deselecting)
    if (newSelection && newSelection.type !== 'waypoint') {
      emitGameEvent('selectEntity', {
        id: newSelection.id,
        type: newSelection.type as 'player' | 'station',
      });
    }
  };

  // Handle view mode change with localStorage persistence
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('astra_view_mode', mode);
  };

  return (
    <div style={hudContainerStyle}>
      {/* Global notification bell (corp invites + asset transfer offers) */}
      <NotificationBell />
      {/* Always-on corporation panel (day-to-day management; founding still requires a station) */}
      <CorporationHUD />
      {/* Top Left - Unified Player Status Panel */}
      <PlayerStatusPanel
        shipName={shipName}
        shipClassType={shipClassType}
        shipTier={shipTier}
        totalPower={totalPower}
        x={x}
        y={y}
        speed={speed}
        zoom={zoom}
        activityStatus={activityStatus}
        reputation={reputation}
        reputationStatus={reputationStatus}
        isProtected={totalPower < 50}
        threatLevel={threatLevel}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        fastTravel={fastTravel}
        onStopFastTravel={() => emitGameEvent('stopFastTravel')}
        followState={followState}
        onStopFollow={() => emitGameEvent('stopFollow')}
        followDistance={followDistance}
        followDistanceMin={FOLLOW_DISTANCE_MIN}
        followDistanceMax={FOLLOW_DISTANCE_MAX}
        onChangeFollowDistance={setFollowDistance}
        warpState={warpState}
        onWarpEngage={() => emitGameEvent('warpEngage')}
        onWarpCancel={() => emitGameEvent('warpCancel')}
        scannerState={{
          hasScanner: radarState.hasRadar,
          isScanning: radarState.isScanning,
          currentRange: radarState.currentRange,
          maxRange: radarState.maxRange,
          scanProgress: radarState.scanProgress,
          scanComplete: radarState.scanComplete,
          isCharging: radarState.isCharging,
          chargeRemaining: radarState.chargeRemaining,
        }}
        isInNebula={isInNebula}
        miningToolInfo={miningToolInfo}
        onStopAllMining={() => emitGameEvent('requestStopMining')}
      />

      {/* Controls button — small "?" next to the status panel */}
      <button
        onClick={() => setShowControls(true)}
        style={{
          position: 'absolute',
          top: '13px',
          left: '240px',
          transform: 'scale(1.3)',
          transformOrigin: 'top left',
          background: `${PALETTE.PANEL}cc`,
          border: `1px solid ${PALETTE.DIM}44`,
          borderRadius: '3px',
          color: PALETTE.DIM,
          fontSize: '9px',
          fontFamily: FONT_DISPLAY,
          padding: '2px 6px',
          cursor: 'pointer',
          transition: 'color 0.15s',
          pointerEvents: 'auto',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = PALETTE.TEAL; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = PALETTE.DIM; }}
        title="Show controls"
      >
        CONTROLS
      </button>

      {/* Controls Modal */}
      {showControls && <ControlsModal onClose={() => setShowControls(false)} />}

      {/* Top Center - Constellation info */}
      <div style={topCenterStyle}>
        <ConstellationInfo name={constellationName} tier={constellationTier} threatLevel={threatLevel} zones={playerZones} />
      </div>

      {/* Police Warning Banner - shown when police are targeting player */}
      {policeWarning && (
        <div
          style={{
            position: 'absolute',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 24px',
            backgroundColor: `${PALETTE.RED}dd`,
            borderRadius: '4px',
            border: `2px solid ${PALETTE.RED}`,
            textAlign: 'center',
            zIndex: 100,
            animation: 'threatPulseRed 0.5s ease-in-out infinite',
          }}
        >
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '14px' }}>
            {policeWarning}
          </div>
        </div>
      )}

      {/* Radiation Warning - shown when player is in a sun's damage zone */}
      {radiationWarning && (
        <div
          style={{
            position: 'absolute',
            top: policeWarning ? '130px' : '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 20px',
            backgroundColor: `rgba(255, ${Math.floor(100 - radiationWarning.proximity * 100)}, 0, ${0.7 + radiationWarning.proximity * 0.3})`,
            borderRadius: '4px',
            border: `2px solid ${radiationWarning.proximity > 0.7 ? '#ff0000' : '#ff6600'}`,
            textAlign: 'center',
            zIndex: 100,
            animation: radiationWarning.proximity > 0.5 ? 'threatPulseRed 0.3s ease-in-out infinite' : undefined,
          }}
        >
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '13px', textTransform: 'uppercase' }}>
            {radiationWarning.proximity > 0.8 ? '☢ LETHAL RADIATION ☢' :
             radiationWarning.proximity > 0.5 ? '☢ EXTREME RADIATION' :
             radiationWarning.proximity > 0.2 ? '☢ HIGH RADIATION' : '☢ RADIATION WARNING'}
          </div>
          <div style={{ color: PALETTE.LIGHT, fontSize: '11px', marginTop: '2px' }}>
            Hull Damage: {radiationWarning.damage}/sec {radiationWarning.proximity > 0.6 && '• EVACUATE IMMEDIATELY'}
          </div>
        </div>
      )}

      {/* Bottom Right - Radar minimap. Inner 70% = AOI (1:1 with on-screen view),
          outer 30% = sqrt-compressed scanner band. Only scanned contacts appear
          beyond the AOI horizon. */}
      <RadarMinimap
        playerX={x}
        playerY={y}
        playerRotation={rotation}
        entities={nearbyEntities as any}
        scannedEntities={radarEntities as any}
        waypoint={waypoint}
        scannerRange={radarState.hasRadar ? radarState.maxRange : undefined}
      />

      {/* Top Right - Overview Panel (replaces credits) */}
      <OverviewPanel
        entities={nearbyEntities}
        scannedEntities={radarEntities.map((e: any) => ({
          id: e.id,
          type: e.type,
          name: e.name,
          x: e.x,
          y: e.y,
          distance: e.distance,
          radius: e.radius,
          resourceType: e.resourceType,
        }))}
        selectedEntity={selectedEntity}
        onSelect={handleEntitySelect}
        isMinimized={overviewMinimized}
        onToggleMinimize={() => setOverviewMinimized(!overviewMinimized)}
        waypoint={waypoint}
        onApproach={(entity: NearbyEntity | null) => {
          if (fastTravel.isActive) {
            emitGameEvent('stopFastTravel');
          } else if (entity) {
            // Always use startFastTravelToTarget with explicit coordinates
            // This ensures we travel to the exact position shown in the HUD,
            // avoiding any potential sync issues with GameScene state
            emitGameEvent('startFastTravelToTarget', {
              x: entity.x,
              y: entity.y,
              name: entity.name,
            });
          }
        }}
        onFollow={(entity: NearbyEntity | null) => {
          // Click on already-followed target → stop. Click on a different entity → switch.
          if (followState.isActive && entity && followState.targetId === entity.id) {
            emitGameEvent('stopFollow');
            return;
          }
          if (!entity) {
            if (followState.isActive) emitGameEvent('stopFollow');
            return;
          }
          const followableTypes = new Set([
            'player', 'asteroid', 'station', 'drop', 'gateway', 'planet',
            'sun', 'nebula', 'drone', 'settlement', 'ice_asteroid', 'gas_pocket', 'wreckage',
          ] as const);
          if (!followableTypes.has(entity.type as never)) return;
          emitGameEvent('startFollow', {
            id: entity.id,
            type: entity.type as 'player' | 'asteroid' | 'station' | 'drop' | 'gateway' | 'planet' | 'sun' | 'nebula' | 'drone' | 'settlement' | 'ice_asteroid' | 'gas_pocket' | 'wreckage',
            name: entity.name,
            distance: followDistance,
          });
        }}
        onClearWaypoint={() => emitGameEvent('clearWaypoint')}
        isApproaching={fastTravel.isActive}
        followState={followState}
        isWarping={warpState.phase === 'calculating' || warpState.phase === 'traveling'}
        miningToolInfo={miningToolInfo}
      />

      {/* Inspect Panel - shown when a target has been scanned with R */}
      {inspectData && (
        <InspectPanel data={inspectData} onClose={() => setInspectData(null)} />
      )}

      {/* Gateway Cloak Indicator - shown when player is cloaked */}
      {cloakState.isCloaked && (
        <div
          style={{
            position: 'absolute',
            bottom: '170px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 20px',
            backgroundColor: `${PALETTE.PANEL}ee`,
            border: '2px solid #8b5cf6',
            borderRadius: '6px',
            textAlign: 'center',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        >
          <div style={{ color: '#8b5cf6', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
            CLOAKED
          </div>
          <div style={{ color: PALETTE.LIGHT, fontSize: '14px', fontWeight: 600 }}>
            {Math.ceil(cloakState.remaining / 1000)}s
          </div>
          <div style={{ color: PALETTE.DIM, fontSize: '10px', marginTop: '2px' }}>
            Invisible to others
          </div>
        </div>
      )}

      {/* Shield drag floating indicator - above status bars */}
      {isShieldDraining && (
        <div
          style={{
            position: 'absolute',
            bottom: '100px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '4px 14px',
            backgroundColor: `${PALETTE.PANEL}cc`,
            borderRadius: '4px',
            border: `1px solid ${PALETTE.ORANGE}88`,
            color: PALETTE.ORANGE,
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '1px',
            pointerEvents: 'none',
            zIndex: 100,
            animation: 'shieldDragPulse 1.2s ease-in-out infinite',
          }}
        >
          SHIELD DRAG
        </div>
      )}

      {/* Bottom Center — Combat-overhaul: 4 spec bars (HULL / HEAT / BOOST / POWER)
          + small SHIELD status indicator (RAISED / DOWN, toggled with Q).
          Legacy SHIELD-HP bar, per-weapon HEAT bar, and AMMO/RELOAD bar were
          removed: shield is heat-based now and there is no ammo. */}
      <div style={bottomCenterStyle}>
        <ThinBar
          label="HULL"
          current={hull}
          max={maxHull}
          color={hull < maxHull * 0.3 ? '#ff2200' : PALETTE.ORANGE}
          width={220}
        />
        <ThinBar
          label={isShipOverheated ? 'OVERLOAD' : 'HEAT'}
          current={shipHeat}
          max={shipMaxHeat}
          color={isShipOverheated ? '#ff2200' : '#ffaa00'}
          activeColor="#ff4400"
          width={220}
          isActive={isShipOverheated}
        />
        {/* Combat-overhaul: boost is native — always show the bar */}
        <ThinBar
          label="BOOST"
          current={boostEnergy}
          max={boostMaxEnergy}
          color={PALETTE.TEAL}
          activeColor="#00ffff"
          width={220}
          isActive={isBoosting}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '60px', textAlign: 'right', color: PALETTE.DIM, fontSize: '9px' }}>
            POWER
          </span>
          <div style={{ width: '220px', height: '6px', display: 'flex', alignItems: 'center' }}>
            <span style={{
              fontSize: '9px',
              fontWeight: 600,
              color: totalPower > powerCapacity ? '#ff2200' : PALETTE.ORANGE,
            }}>
              {totalPower}/{powerCapacity}
            </span>
          </div>
        </div>
        {/* SHIELD status (Q toggles) — kept as a compact text indicator since
            the actual shield field is drawn on the ship itself, not here. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
          <span style={{ width: '60px', textAlign: 'right', color: PALETTE.DIM, fontSize: '9px' }}>
            SHIELD
          </span>
          <div style={{
            width: '220px',
            height: '6px',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '6px',
            color: isVenting ? '#ff8800' : shieldActive ? PALETTE.TEAL : PALETTE.DIM,
            fontWeight: 600,
            fontSize: '9px',
            letterSpacing: '1px',
          }}>
            {isVenting ? 'VENTING…' : shieldActive ? 'RAISED (Q) — VENT (V)' : 'DOWN (Q) — VENT (V)'}
          </div>
        </div>
        {/* Active weapon indicator (Tab cycles) */}
        {activeWeaponName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
            <span style={{ width: '60px', textAlign: 'right', color: PALETTE.DIM, fontSize: '9px' }}>
              WEAPON
            </span>
            <div style={{
              width: '220px',
              height: '6px',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '6px',
              gap: '6px',
            }}>
              <span style={{
                fontSize: '9px',
                fontWeight: 600,
                color: PALETTE.ORANGE,
                letterSpacing: '1px',
              }}>
                {activeWeaponName.toUpperCase()}
              </span>
              {equippedWeaponCount > 1 && (
                <span style={{
                  fontSize: '8px',
                  color: PALETTE.DIM,
                }}>
                  {activeWeaponIndex + 1}/{equippedWeaponCount} (Tab)
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dock Prompt - shown when near a station */}
      {dockPrompt.show && !fastTravel.isActive && (
        <div
          style={{
            position: 'absolute',
            bottom: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: `${PALETTE.PANEL}ee`,
            border: `2px solid ${PALETTE.TEAL}`,
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
            {dockPrompt.stationName || 'Station'}
          </div>
          <div style={{ color: PALETTE.TEAL, fontSize: '12px' }}>
            Press [E] to dock
          </div>
        </div>
      )}

      {/* Mining prompt removed — mining is now triggered via the MINE button in the Selected Item panel */}

      {/* Gateway Prompt - shown when near a gateway */}
      {gatewayPrompt.show && !fastTravel.isActive && !dockPrompt.show && !miningPrompt.show && (
        <div
          style={{
            position: 'absolute',
            bottom: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: `${PALETTE.PANEL}ee`,
            border: '2px solid #00dddd',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
            Jump to {gatewayPrompt.targetConstellationName || 'Unknown Constellation'}
          </div>
          <div style={{ color: '#00dddd', fontSize: '12px' }}>
            Press [J] to travel ({gatewayPrompt.travelCost || 0} cr)
          </div>
        </div>
      )}

      {/* Drop Pickup Prompt */}
      {dropPrompt.show && !fastTravel.isActive && !dockPrompt.show && (
        <div
          style={{
            position: 'absolute',
            bottom: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: `${PALETTE.PANEL}ee`,
            border: `2px solid ${dropPrompt.canAccess ? '#ffaa00' : '#888888'}`,
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
            {dropPrompt.ownerOnly && !dropPrompt.canAccess ? 'Protected Loot' : 'Loot Container'}
          </div>
          <div style={{ color: dropPrompt.canAccess ? '#ffaa00' : '#888888', fontSize: '12px' }}>
            {dropPrompt.canAccess ? 'Press [E] to loot' : 'Owned by another captain (60s)'}
          </div>
        </div>
      )}

      {/* Planet Landing Prompt - shown when near a planet */}
      {planetLandingPrompt.show && !fastTravel.isActive && !dockPrompt.show && !miningPrompt.show && !gatewayPrompt.show && (
        <div
          style={{
            position: 'absolute',
            bottom: '120px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            backgroundColor: `${PALETTE.PANEL}ee`,
            border: '2px solid #44cc44',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ color: PALETTE.LIGHT, fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
            {planetLandingPrompt.planetName || 'Unknown Planet'}
          </div>
          <div style={{ color: '#44cc44', fontSize: '12px' }}>
            Press E to Enter
          </div>
        </div>
      )}

      {/* Warp travel vignette - intra-constellation. World stays visible (warp is
          "visible and vulnerable") but the edges darken and faint streaks convey
          speed while crossing the constellation. */}
      {warpState.phase === 'traveling' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 900,
            background:
              'radial-gradient(ellipse at center, transparent 32%, rgba(0,30,60,0.30) 68%, rgba(0,10,25,0.72) 100%)',
            animation: 'warpVignettePulse 1.6s ease-in-out infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.5,
              backgroundImage:
                'repeating-linear-gradient(to bottom, transparent 0px, rgba(120,200,255,0.10) 2px, transparent 5px)',
              backgroundSize: '100% 1400px',
              animation: 'warpStreaks 0.6s linear infinite',
              maskImage:
                'radial-gradient(ellipse at center, transparent 30%, #000 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, transparent 30%, #000 80%)',
            }}
          />
        </div>
      )}

      {/* Warp arrival flash - masks the one-shot destination AOI reload on warp exit. */}
      {warpArrivalFlash && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 901,
            background:
              'radial-gradient(ellipse at center, rgba(180,225,255,0.85) 0%, rgba(0,140,255,0.5) 45%, rgba(0,20,50,0.0) 100%)',
            animation: 'warpArrivalFade 0.45s ease-out forwards',
          }}
        />
      )}

      {/* Gateway Transition Overlay - shown during constellation travel */}
      {gatewayTransition.show && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#000',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'fadeIn 0.3s ease-in',
          }}
        >
          <WarpCanvas style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              textShadow: '0 0 12px rgba(0,0,0,0.85)',
            }}
          >
            <div style={{ color: '#00dddd', fontSize: '18px', fontWeight: 700, marginBottom: '16px', letterSpacing: '0.15em' }}>
              JUMPING...
            </div>
            <div style={{ color: PALETTE.LIGHT, fontSize: '16px' }}>
              Traveling to {gatewayTransition.targetConstellationName || 'destination'}
            </div>
          </div>
        </div>
      )}

      {/* Waypoint Compass - shown on screen edge when waypoint is off-screen */}
      {waypoint && !waypoint.isOnScreen && !fastTravel.isActive && (
        <WaypointCompass
          angle={waypoint.angle}
          distance={waypoint.distance}
          name={waypoint.name}
        />
      )}

      {/* Edge indicators for all overview entities (players, stations, gateways) */}
      {nearbyEntities
        .filter((e) => e.type === 'player' || e.type === 'station' || e.type === 'gateway')
        .map((entity) => (
          <EntityEdgeIndicator
            key={`edge-${entity.id}`}
            entity={entity}
            playerX={x}
            playerY={y}
            zoom={zoom}
          />
        ))}

      {/* Notification banner - centered above status bars */}
      {notification && (
        <div
          style={{
            position: 'absolute',
            bottom: '140px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 24px',
            backgroundColor: `${PALETTE.PANEL}ee`,
            borderRadius: '4px',
            border: `1px solid ${notification.type === 'info' ? PALETTE.TEAL : notification.type === 'success' ? PALETTE.GREEN : notification.type === 'warning' ? PALETTE.ORANGE : '#ff4444'}`,
            color: notification.type === 'info' ? PALETTE.TEAL : notification.type === 'success' ? PALETTE.GREEN : notification.type === 'warning' ? PALETTE.ORANGE : '#ff4444',
            fontSize: '14px',
            fontWeight: 600,
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          {notification.message}
        </div>
      )}

      {lootModal && (
        <DropLootModal
          dropId={lootModal.dropId}
          items={lootModal.items}
          onClose={() => setLootModal(null)}
        />
      )}

      {shipBayOpen && (
        <ShipBayOverlay onClose={() => setShipBayOpen(false)} />
      )}

      <DevSpawnerPanel />

      {/* Cargo Scan Result popup */}
      {cargoScanResult && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: `${PALETTE.PANEL}ee`,
            border: `2px solid ${PALETTE.TEAL}`,
            borderRadius: '8px',
            padding: '16px 20px',
            minWidth: '250px',
            maxWidth: '350px',
            pointerEvents: 'auto',
            zIndex: 200,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div>
              <div style={{ color: PALETTE.TEAL, fontSize: '11px', fontWeight: 600, letterSpacing: '1px' }}>
                CARGO SCAN
              </div>
              <div style={{ color: PALETTE.LIGHT, fontSize: '14px', fontWeight: 600 }}>
                {cargoScanResult.targetName}
              </div>
              <div style={{ color: PALETTE.DIM, fontSize: '10px' }}>
                {cargoScanResult.shipClass} &middot; {cargoScanResult.cargo.used}/{cargoScanResult.cargo.capacity} cargo
              </div>
            </div>
            <div
              onClick={() => setCargoScanResult(null)}
              style={{ color: PALETTE.DIM, cursor: 'pointer', fontSize: '16px', padding: '4px' }}
            >
              ✕
            </div>
          </div>
          {cargoScanResult.cargo.items.length === 0 ? (
            <div style={{ color: PALETTE.DIM, fontSize: '11px', textAlign: 'center', padding: '8px' }}>
              Cargo is empty
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {cargoScanResult.cargo.items.map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  backgroundColor: `${PALETTE.DARK}88`,
                  borderRadius: '3px',
                  fontSize: '11px',
                }}>
                  <span style={{ color: item.itemType === 'resource' ? PALETTE.YELLOW : PALETTE.TEAL }}>
                    {item.itemId.replace(/_/g, ' ')}
                  </span>
                  <span style={{ color: PALETTE.LIGHT, fontWeight: 600 }}>
                    x{item.quantity}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Utility Hotbar - bottom right, keys 1-5 */}
      <UtilityHotbar />

    </div>
  );
}

// Entity edge indicator - shows direction arrows on screen edges for off-screen entities
function EntityEdgeIndicator({
  entity,
  playerX,
  playerY,
  zoom,
}: {
  entity: NearbyEntity;
  playerX: number;
  playerY: number;
  zoom: number;
}) {
  // Calculate world-space offset from player to entity
  const dx = entity.x - playerX;
  const dy = entity.y - playerY;

  // Convert to screen-space position (player is at screen center)
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const screenDx = dx * zoom;
  const screenDy = dy * zoom;

  // Check if entity is visible on screen (with some margin for entity size)
  const margin = 50; // Account for entity graphics size
  const halfScreenW = screenWidth / 2 - margin;
  const halfScreenH = screenHeight / 2 - margin;

  if (Math.abs(screenDx) < halfScreenW && Math.abs(screenDy) < halfScreenH) {
    // Entity is on screen, don't show edge indicator
    return null;
  }

  // Calculate angle from player to entity
  // Game world uses PixiJS coordinates where Y increases downward (same as screen)
  const angle = Math.atan2(dy, dx);

  // Calculate position on screen edge
  const centerX = screenWidth / 2;
  const centerY = screenHeight / 2;
  const edgeMargin = 30;

  const halfW = centerX - edgeMargin;
  const halfH = centerY - edgeMargin;

  const tanAngle = Math.tan(angle);
  const cosAngle = Math.cos(angle);
  const sinAngle = Math.sin(angle);

  let edgeX: number, edgeY: number;
  if (Math.abs(cosAngle) * halfH > Math.abs(sinAngle) * halfW) {
    edgeX = cosAngle > 0 ? halfW : -halfW;
    edgeY = edgeX * tanAngle;
  } else {
    edgeY = sinAngle > 0 ? halfH : -halfH;
    edgeX = edgeY / tanAngle;
  }

  // Clamp to screen bounds
  edgeX = Math.max(-halfW, Math.min(halfW, edgeX));
  edgeY = Math.max(-halfH, Math.min(halfH, edgeY));

  const posX = centerX + edgeX;
  // No Y negation needed - game and screen both use Y increasing downward
  const posY = centerY + edgeY;

  // Rotation for arrow (pointing toward entity)
  // Add 90 to orient the arrow (arrow border-bottom points up at 0deg)
  const arrowRotation = angle * (180 / Math.PI) + 90;

  // Get color based on entity type
  const config = entityTypeConfig[entity.type];
  const color = config?.color || PALETTE.DIM;

  return (
    <div
      style={{
        position: 'absolute',
        left: posX,
        top: posY,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Small arrow pointing to entity */}
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderBottom: `10px solid ${color}`,
          transform: `rotate(${arrowRotation}deg)`,
          opacity: 0.8,
        }}
      />
    </div>
  );
}

// Waypoint compass indicator on screen edge
function WaypointCompass({
  angle,
  distance,
}: {
  angle: number;
  distance: number;
  name: string;
}) {
  // Calculate position on screen edge
  // Game world uses PixiJS coordinates where Y increases downward (same as screen)
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const centerX = screenWidth / 2;
  const centerY = screenHeight / 2;
  const edgeMargin = 30;

  const halfW = centerX - edgeMargin;
  const halfH = centerY - edgeMargin;

  const tanAngle = Math.tan(angle);
  const cosAngle = Math.cos(angle);
  const sinAngle = Math.sin(angle);

  let edgeX: number, edgeY: number;
  if (Math.abs(cosAngle) * halfH > Math.abs(sinAngle) * halfW) {
    edgeX = cosAngle > 0 ? halfW : -halfW;
    edgeY = edgeX * tanAngle;
  } else {
    edgeY = sinAngle > 0 ? halfH : -halfH;
    edgeX = edgeY / tanAngle;
  }

  // Clamp to screen bounds
  edgeX = Math.max(-halfW, Math.min(halfW, edgeX));
  edgeY = Math.max(-halfH, Math.min(halfH, edgeY));

  const posX = centerX + edgeX;
  // No Y negation needed - game and screen both use Y increasing downward
  const posY = centerY + edgeY;

  // Rotation for arrow (pointing toward waypoint)
  // Add 90 to orient the arrow (arrow border-bottom points up at 0deg)
  const arrowRotation = angle * (180 / Math.PI) + 90;

  return (
    <div
      style={{
        position: 'absolute',
        left: posX,
        top: posY,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Arrow pointing to waypoint */}
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderBottom: `14px solid ${PALETTE.ORANGE}`,
          transform: `rotate(${arrowRotation}deg)`,
          marginBottom: '4px',
        }}
      />
      {/* Distance text */}
      <div
        style={{
          backgroundColor: `${PALETTE.PANEL}cc`,
          padding: '2px 6px',
          borderRadius: '2px',
          fontSize: '9px',
          color: PALETTE.ORANGE,
          whiteSpace: 'nowrap',
        }}
      >
        {formatDistance(distance)}
      </div>
    </div>
  );
}

// Format distance for display
function formatDistance(distance: number): string {
  if (distance < 1000) {
    return `${Math.round(distance)}m`;
  } else {
    return `${(distance / 1000).toFixed(1)}km`;
  }
}

// Format time for display
function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  }
}

export default GameHUD;
